import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ResponsiveContainer, LineChart as RLineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Play, X, Trash2, AlertTriangle, Download, RefreshCw, GitCompareArrows, ArrowUp, ArrowDown, ArrowUpDown, EyeOff, Copy, Search } from "lucide-react";
import { api, fmtUsd, fmtPct, fmtTime } from "@/lib/api";
import { SectionTitle, StatCard, Badge } from "@/components/Bits";
import { StrategyPicker, StrategySummary, DiagnosticsRow } from "@/components/StrategyPicker";
import { RESEARCH_PRESETS, findPreset } from "@/lib/researchPresets";
import { ResearchCachePill } from "@/components/ResearchCachePill";
import { WarmAllPresetsButton } from "@/components/WarmAllPresetsButton";
import { CacheTelemetryBadge } from "@/components/CacheTelemetryBadge";
import RegimePresetCachePanel from "@/components/RegimePresetCachePanel";
import { loadResearchPreset, persistResearchPreset } from "@/lib/researchPresetStorage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const isoToInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Locale-safe numeric parser for backtest form inputs.
 *
 * Why this exists: production users on pt-BR (Brazilian Portuguese)
 * locale see HTML5 `<input type="number">` accept decimal commas
 * (`0,8` instead of `0.8`). The native form value is a string with
 * the locale's separator. Plain `parseFloat("0,8")` returns `0`
 * (stops at the comma) — silently zeroing out user-meaningful
 * thresholds like `min_price_move_between_same_side_entries_pct`,
 * `fee_rate`, `slippage_bps`. The user changes the value in the UI
 * and gets identical results because the backend receives 0 every
 * time.
 *
 * This parser:
 *   - Accepts plain numbers verbatim.
 *   - Accepts strings with a single `,` or `.` decimal separator —
 *     normalised to `.` before parsing.
 *   - Returns NaN on truly invalid input so callers can decide
 *     whether to default or surface an error.
 *
 * Limitation: does NOT attempt to parse thousand-separators
 * (`1,234.56` or `1.234,56`). Backtest inputs are always small
 * positive numbers; if a user wants 1,234 BTC they're already in a
 * different problem domain.
 */
function parseLocaleFloat(raw) {
  if (raw === null || raw === undefined || raw === "") return NaN;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : NaN;
  const s = String(raw).trim();
  if (!s) return NaN;
  // Accept either single-comma OR single-dot decimal. Bail if both.
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  let normalized = s;
  if (hasComma && !hasDot) normalized = s.replace(",", ".");
  // (`hasDot && hasComma` left as-is → parseFloat will likely NaN
  // on `1,234.56` style; we don't want to guess thousand separators.)
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : NaN;
}

function parseLocaleInt(raw) {
  const f = parseLocaleFloat(raw);
  return Number.isFinite(f) ? Math.trunc(f) : NaN;
}


const defaultStart = () => { const d = new Date(); d.setDate(d.getDate() - 7); return isoToInput(d.toISOString()); };
const defaultEnd = () => isoToInput(new Date().toISOString());

const DATE_PRESETS = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "180d", label: "Last 180 days", days: 180 },
  { key: "365d", label: "Last 365 days", days: 365 },
];

// Default fee rate represents the Binance Futures market taker fee (0.05% = 0.0005).
// Old default was 0.0004 (4 bps); 0.0005 (5 bps) is more representative of what a
// real fill costs in production. The user can override this and we persist their
// choice across sessions in localStorage — no backend schema change required.
// ---------------------------------------------------------------------------
// Backtest UX persistence (localStorage). Frontend-only — no backend/API
// contract change. Survives page reloads + cross-page navigation so the user
// doesn't have to re-pick their date range every time they hop between
// Backtest, Strategy Profiles, and Compare Backtests.
// ---------------------------------------------------------------------------
const FEE_RATE_DEFAULT = 0.0005;
const FEE_RATE_LS_KEY = "btcbot.backtest.fee_rate";

const RANGE_LS_START_KEY = "btcbot.backtest.start_date";
const RANGE_LS_END_KEY = "btcbot.backtest.end_date";
const RANGE_LS_PRESET_KEY = "btcbot.backtest.active_preset";
const PROFILE_LS_KEY = "btcbot.backtest.selected_profile_id";
// Whitelist of preset keys we accept from localStorage. Anything else
// (e.g. a future-renamed key, garbage, deliberate tampering) falls back
// to the default 7d range.
const VALID_PRESET_KEYS = new Set(["7d", "30d", "90d", "180d", "365d", "custom"]);

function _validISOInputDate(s) {
  if (typeof s !== "string" || s.length < 10) return false;
  const d = new Date(s);
  return !Number.isNaN(d.getTime());
}

// Loads the persisted (start, end, activePresetKey). Returns sane defaults
// (Last 7 days) on ANY failure path: missing keys, malformed strings,
// corrupted timestamps, or end<=start.
function loadPersistedRange() {
  const fallback = {
    start_iso: defaultStart(),
    end_iso: defaultEnd(),
    activePresetKey: "7d",
  };
  try {
    if (typeof window === "undefined" || !window.localStorage) return fallback;
    const startRaw = window.localStorage.getItem(RANGE_LS_START_KEY);
    const endRaw = window.localStorage.getItem(RANGE_LS_END_KEY);
    const presetRaw = window.localStorage.getItem(RANGE_LS_PRESET_KEY);
    // Treat "first-ever load" (no keys present at all) as fallback so
    // regression tests + brand-new users get the documented 7d default.
    if (startRaw == null && endRaw == null && presetRaw == null) return fallback;
    if (!_validISOInputDate(startRaw) || !_validISOInputDate(endRaw)) return fallback;
    if (new Date(endRaw) <= new Date(startRaw)) return fallback;
    const preset = VALID_PRESET_KEYS.has(presetRaw) ? presetRaw : "custom";
    return { start_iso: startRaw, end_iso: endRaw, activePresetKey: preset };
  } catch {
    return fallback;
  }
}

function persistRange({ start_iso, end_iso, activePresetKey }) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (start_iso) window.localStorage.setItem(RANGE_LS_START_KEY, start_iso);
    if (end_iso) window.localStorage.setItem(RANGE_LS_END_KEY, end_iso);
    if (activePresetKey && VALID_PRESET_KEYS.has(activePresetKey)) {
      window.localStorage.setItem(RANGE_LS_PRESET_KEY, activePresetKey);
    }
  } catch {
    /* swallow — persistence is best-effort */
  }
}

// Last-selected strategy profile persistence. We only store the id — never
// the profile body — so an edit to the profile elsewhere is automatically
// picked up on return (we always re-fetch the latest definition from the
// API on mount and look up by id). Caller is responsible for clearing the
// key when the profile is found to no longer exist.
function loadPersistedProfileId() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(PROFILE_LS_KEY);
    if (!raw || typeof raw !== "string") return null;
    return raw;
  } catch {
    return null;
  }
}

function persistProfileId(id) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (id) window.localStorage.setItem(PROFILE_LS_KEY, String(id));
    else window.localStorage.removeItem(PROFILE_LS_KEY);
  } catch {
    /* swallow */
  }
}

function loadPersistedFeeRate() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return FEE_RATE_DEFAULT;
    const raw = window.localStorage.getItem(FEE_RATE_LS_KEY);
    if (raw == null) return FEE_RATE_DEFAULT;
    const n = parseFloat(raw);
    // Server enforces `fee_rate ∈ [0, 0.01]`; mirror those bounds + reject NaN
    // so a corrupted localStorage value (e.g. user fiddling in devtools)
    // can never produce an unsubmittable form.
    if (!Number.isFinite(n) || n < 0 || n > 0.01) return FEE_RATE_DEFAULT;
    return n;
  } catch {
    return FEE_RATE_DEFAULT;
  }
}

function persistFeeRate(value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n < 0 || n > 0.01) return;
    window.localStorage.setItem(FEE_RATE_LS_KEY, String(n));
  } catch {
    /* swallow — persistence is best-effort */
  }
}

// ---------------------------------------------------------------------------
// Remaining backtest form-field persistence (initial_balance, leverage,
// order_size_mode, order_size_value, hedge_mode, slippage_bps,
// liquidations_mode). One JSON blob keyed by `BACKTEST_FORM_LS_KEY` —
// loaded on mount, written on every relevant change. Bounds-checked
// against server-side limits (`server.py:710` BacktestRunRequest) so a
// tampered LS value can never produce an unsubmittable form. Falls back
// silently to per-field defaults when missing/corrupt.
// ---------------------------------------------------------------------------
const BACKTEST_FORM_LS_KEY = "btcbot.backtest.form_fields";

const FORM_FIELD_DEFAULTS = {
  initial_balance: 10000,
  leverage: 5,
  order_size_mode: "usdt",
  order_size_value: 200,
  // Default OFF — keeps backtest realistic for LBank one-way live.
  // The toggle stays visible for users who want to research hedge
  // setups or paper-trade with both sides; turning it ON surfaces an
  // inline LBank warning + tags the run as `LBank-unrealistic` when
  // the profile allows max_open_positions>=2.
  hedge_mode: false,
  // Per-side stacking caps. Defaults to 1/1 = no stacking (matches
  // LBank one-way live default). Bump to 2+ to model same-side
  // stacking strategies.
  max_open_long_positions: 1,
  max_open_short_positions: 1,
  // Stacking-realism filters (mirror live risk_settings). Default
  // 0/0 = disabled; bump to enforce time and price-move gates on
  // stacked entries (essential to avoid over-counting trades when
  // simulating stacking).
  min_time_between_trades_seconds: 0,
  min_price_move_between_same_side_entries_pct: 0,
  slippage_bps: 0,
  liquidations_mode: "skip",
  // Quick Mode — skips heavy analytics + shrinks payload. Core
  // metrics (trades, PnL, equity, DD, PF, WR, fees) byte-identical.
  // Default OFF to preserve full result on first run. Persisted in
  // localStorage like the other form fields.
  quick_mode: false,
};

const VALID_ORDER_SIZE_MODES = new Set(["usdt", "percent"]);
const VALID_LIQUIDATIONS_MODES = new Set(["skip", "force_close"]);

// Single source of truth for per-field validation. Returns the sanitized
// value or `undefined` to signal "use default". Mirrors the FastAPI
// Pydantic constraints in `server.py:BacktestRunRequest`.
function _sanitizeFormField(key, raw) {
  if (raw === undefined || raw === null) return undefined;
  switch (key) {
    case "initial_balance": {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1 || n > 1_000_000_000) return undefined;
      return n;
    }
    case "leverage": {
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > 125) return undefined;
      return n;
    }
    case "order_size_mode":
      return VALID_ORDER_SIZE_MODES.has(raw) ? raw : undefined;
    case "order_size_value": {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return n;
    }
    case "hedge_mode":
      return typeof raw === "boolean" ? raw : undefined;
    case "max_open_long_positions":
    case "max_open_short_positions": {
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > 20) return undefined;
      return n;
    }
    case "min_time_between_trades_seconds": {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 86_400) return undefined;
      return n;
    }
    case "min_price_move_between_same_side_entries_pct": {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 100) return undefined;
      return n;
    }
    case "slippage_bps": {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 1000) return undefined;
      return n;
    }
    case "liquidations_mode":
      return VALID_LIQUIDATIONS_MODES.has(raw) ? raw : undefined;
    case "quick_mode":
      return typeof raw === "boolean" ? raw : undefined;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Risk-Settings prefill — pure merge helper.
// ---------------------------------------------------------------------------
// Computes which of the four backtest mirror fields should be prefilled from
// the live `risk_settings` document. Returns `{ updates, flags }` where:
//   * `updates` — partial form patch (only the keys that should change).
//   * `flags`   — `prefilledFromRisk` patch (only the keys whose visible
//     "(prefilled from Risk Settings)" hint should turn ON).
//
// Rules:
//   * `risk` falsy / non-object → no-op (defensive against API failures).
//   * Fields that are `undefined` / `null` on the risk doc are skipped.
//   * Fields the user has explicitly touched (snapshot rerun, manual edit)
//     are skipped UNLESS `force=true` (the explicit "Sync" button).
//
// Kept as a pure function for unit testing; no React, no I/O, no closures.
const RISK_PREFILL_KEYS = [
  "max_open_long_positions",
  "max_open_short_positions",
  "min_time_between_trades_seconds",
  "min_price_move_between_same_side_entries_pct",
];

function _computeRiskPrefillUpdates(risk, touched, force = false) {
  const empty = { updates: {}, flags: {} };
  if (!risk || typeof risk !== "object") return empty;
  const touchedSet =
    touched instanceof Set ? touched : new Set(Array.isArray(touched) ? touched : []);
  const updates = {};
  const flags = {};
  for (const k of RISK_PREFILL_KEYS) {
    const v = risk[k];
    if (v === undefined || v === null) continue;
    if (!force && touchedSet.has(k)) continue;
    updates[k] = v;
    flags[k] = true;
  }
  return { updates, flags };
}

function loadPersistedFormFields() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return FORM_FIELD_DEFAULTS;
    const raw = window.localStorage.getItem(BACKTEST_FORM_LS_KEY);
    if (!raw) return FORM_FIELD_DEFAULTS;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return FORM_FIELD_DEFAULTS;
    const out = { ...FORM_FIELD_DEFAULTS };
    for (const k of Object.keys(FORM_FIELD_DEFAULTS)) {
      const sanitized = _sanitizeFormField(k, parsed[k]);
      if (sanitized !== undefined) out[k] = sanitized;
    }
    return out;
  } catch {
    return FORM_FIELD_DEFAULTS;
  }
}

function persistFormFields(form) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const payload = {};
    for (const k of Object.keys(FORM_FIELD_DEFAULTS)) {
      const sanitized = _sanitizeFormField(k, form[k]);
      payload[k] = sanitized === undefined ? FORM_FIELD_DEFAULTS[k] : sanitized;
    }
    window.localStorage.setItem(BACKTEST_FORM_LS_KEY, JSON.stringify(payload));
  } catch {
    /* swallow — persistence is best-effort */
  }
}

export default function Backtest() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStrategyId = searchParams.get("strategy_id");

  // Restore the user's last-used range + active preset from localStorage.
  // Computed once on mount; subsequent state changes fan out to LS via the
  // mutation paths below (applyPreset / date input onChange).
  const _persistedRange = loadPersistedRange();
  const _persistedFormFields = loadPersistedFormFields();
  const [form, setForm] = useState({
    symbol: "BTCUSDT",
    start_iso: _persistedRange.start_iso,
    end_iso: _persistedRange.end_iso,
    // Spread persisted values FIRST so any new field added to
    // `FORM_FIELD_DEFAULTS` is automatically picked up without
    // having to remember to list it here. (Fixes a latent bug
    // where the four stacking fields were missing from the
    // explicit list and rendered as uncontrolled inputs.)
    ..._persistedFormFields,
    fee_rate: loadPersistedFeeRate(),
  });
  // Prefill marker: which fields the user has explicitly edited
  // since this page mount. Mutations from snapshot rerun /
  // duplicate / direct user keystrokes set the relevant key to
  // true, blocking the Risk-Settings prefill effect from
  // clobbering the user's intent on a delayed fetch arrival.
  const userTouchedRef = React.useRef(new Set());
  // Visible "(prefilled from Risk Settings)" hint on the four
  // mirror fields. Cleared per-field as soon as the user types
  // or a rerun overrides the value.
  const [prefilledFromRisk, setPrefilledFromRisk] = useState({
    max_open_long_positions: false,
    max_open_short_positions: false,
    min_time_between_trades_seconds: false,
    min_price_move_between_same_side_entries_pct: false,
  });
  // -----------------------------------------------------------------
  // Risk-Settings prefill effect
  // -----------------------------------------------------------------
  // On mount, fetch the live `risk_settings` and copy the four
  // backtest-mirror fields into the form so a fresh backtest starts
  // matching live by default. Avoids the friction of retyping the
  // same values every run AND the silent parity drift that follows
  // from forgetting one.
  //
  // Rules (encoded below):
  //   * Skipped per-field if the user has touched that field this
  //     session (`userTouchedRef`) — so the prefill cannot clobber
  //     manual intent if the fetch resolves late.
  //   * Skipped per-field if a rerun has already populated it via
  //     `_prefillFromDoc` (which calls `markUserTouched` so the
  //     snapshot wins).
  //   * Skipped silently on fetch failure — backtest must NEVER
  //     block on a Risk-Settings hiccup; defaults stay in place.
  //   * Re-runnable via the "↻ Sync from Risk Settings" button.
  // -----------------------------------------------------------------
  const _applyRiskPrefill = async (force = false) => {
    try {
      const r = await api.getRisk();
      const { updates, flags } = _computeRiskPrefillUpdates(
        r,
        userTouchedRef.current,
        force,
      );
      if (Object.keys(updates).length > 0) {
        setForm((f) => ({ ...f, ...updates }));
        setPrefilledFromRisk((p) => ({ ...p, ...flags }));
      }
    } catch (e) {
      // Fail-silent: backtest page must keep working without
      // a successful risk-settings fetch.
      // eslint-disable-next-line no-console
      console.warn("[Backtest] Risk-settings prefill skipped:", e?.message || e);
    }
  };
  useEffect(() => {
    _applyRiskPrefill(false);
    // Empty deps — runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wraps `setForm` for the four mirror fields so we record the
  // user's intent and clear the "(prefilled)" hint on first edit.
  const markUserTouched = (key) => {
    userTouchedRef.current.add(key);
    if (key in prefilledFromRisk) {
      setPrefilledFromRisk((p) => ({ ...p, [key]: false }));
    }
  };
  const [picker, setPicker] = useState({ mode: "active", profileId: null });
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [running, setRunning] = useState(null); // { id }
  const [doc, setDoc] = useState(null);
  const [list, setList] = useState([]);
  const [activePresetKey, setActivePresetKey] = useState(_persistedRange.activePresetKey);
  // Research Window preset selector — operator-curated BTC market
  // regimes (Uptrend 1/2, Chop 1/2, Strong Downtrend, Composite ...).
  // Independent from the Quick-Range buttons above so the operator
  // can mix-and-match (pick "Strong Downtrend 1", then nudge the
  // end date by a day manually — works exactly like a Custom range).
  const [researchPresetId, setResearchPresetId] = useState(() => loadResearchPreset("backtest", "custom"));
  // Temporary experimental Regime Direction Gate toggle for BT runs.
  // No styling polish — single switch + tf picker. When ON, the
  // launcher attaches a `regime_direction_gate` block to the
  // inline_profile snapshot sent to the backend. Default OFF →
  // bit-identical baseline. Persisted in localStorage so the choice
  // survives page refresh during A/B comparison sessions.
  const [regimeGate, setRegimeGate] = useState(() => {
    try {
      const raw = window.localStorage.getItem("bt.regime_gate");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { enabled: false, regime_timeframe: "2h" };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("bt.regime_gate", JSON.stringify(regimeGate));
    } catch { /* ignore */ }
  }, [regimeGate]);
  const [compositeRunning, setCompositeRunning] = useState(null); // {id}
  const [compositeDoc, setCompositeDoc] = useState(null);
  // Destructive bulk-cleanup state (profit-factor floor). Two-step flow:
  //   1. User types a PF threshold and clicks "Delete below PF" → we POST
  //      to /preview to get an exact matching count with ZERO side effects.
  //   2. Confirmation dialog shows threshold + count. Only after explicit
  //      confirm do we POST to /cleanup.
  const [cleanupPf, setCleanupPf] = useState("1.2");
  const [cleanupPreview, setCleanupPreview] = useState(null); // {threshold, matching_count, total_count} | null
  const [cleanupBusy, setCleanupBusy] = useState(false);

  // ---- Recent Backtests: TF-bulk-delete + sort + exclude-TF filter ----
  // `bulkDeleteTfs`     : array of TFs the user has CHECKED for destructive delete
  // `bulkDeletePreview` : null | {filters, matching_count, total_count}
  // `bulkBusy`          : true while server call in flight
  // `excludeTfs`        : array of TFs the user wants HIDDEN from the table
  // `sortKey`/`sortDir` : current sort. `created_at` desc is the safe default.
  const [bulkDeleteTfs, setBulkDeleteTfs] = useState([]);
  const [bulkDeletePreview, setBulkDeletePreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [excludeTfs, setExcludeTfs] = useState(() => {
    try {
      const raw = localStorage.getItem("backtest_history_exclude_tfs");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
    } catch { return []; }
  });
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem("backtest_history_sort_key") || "created_at"; }
    catch { return "created_at"; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem("backtest_history_sort_dir") || "desc"; }
    catch { return "desc"; }
  });
  // Free-text search across config_tags + strategy name + strategy id.
  // Persisted alongside the other history-table state so a refresh
  // doesn't lose the operator's filter.
  const [searchText, setSearchText] = useState(() => {
    try { return localStorage.getItem("backtest_history_search") || ""; }
    catch { return ""; }
  });
  // Research-regime filter for history table. Reads
  // `row.config.research_regime_type` (set by the new Research Window
  // preset metadata). Pinned to "all" by default so existing rows
  // without the field continue to render unchanged.
  const [regimeFilter, setRegimeFilter] = useState(() => {
    try { return localStorage.getItem("backtest_history_regime_filter") || "all"; }
    catch { return "all"; }
  });

  // When the operator clicks "Rerun" or "Duplicate" on a completed
  // backtest, the form is pre-filled with that run's exact config AND
  // — critically — the strategy is sent as an `inline_profile` snapshot
  // (NOT the live profile id). This guarantees rerunning produces an
  // identical run regardless of subsequent live-strategy edits. We
  // store the snapshot here in component state; the `run` body builder
  // attaches it when present and clears it after submission.
  const [inlineProfileOverride, setInlineProfileOverride] = useState(null);
  // Friendly label for the override badge (so the operator sees what's
  // primed without expanding the JSON viewer).
  const [overrideLabel, setOverrideLabel] = useState("");
  // Persist sort + exclude filter so a refresh keeps the user's view.
  useEffect(() => {
    try { localStorage.setItem("backtest_history_sort_key", sortKey); } catch {}
  }, [sortKey]);
  useEffect(() => {
    try { localStorage.setItem("backtest_history_sort_dir", sortDir); } catch {}
  }, [sortDir]);
  useEffect(() => {
    try { localStorage.setItem("backtest_history_exclude_tfs", JSON.stringify(excludeTfs)); } catch {}
  }, [excludeTfs]);
  useEffect(() => {
    try { localStorage.setItem("backtest_history_search", searchText); } catch {}
  }, [searchText]);
  useEffect(() => {
    try { localStorage.setItem("backtest_history_regime_filter", regimeFilter); } catch {}
  }, [regimeFilter]);

  // Persist all backtest-form input fields (initial_balance, leverage,
  // order_size_*, hedge_mode, slippage_bps, liquidations_mode) so the
  // user's last-used inputs survive refresh / re-login / browser
  // close. `start_iso`/`end_iso`/`activePresetKey` are persisted via
  // their own dedicated paths (see persistRange). `fee_rate` is
  // persisted via persistFeeRate. This effect handles the remaining 7
  // fields as a single JSON blob — cheaper than 7 keys, atomic write.
  useEffect(() => {
    persistFormFields(form);
  }, [
    form.initial_balance,
    form.leverage,
    form.order_size_mode,
    form.order_size_value,
    form.hedge_mode,
    form.slippage_bps,
    form.liquidations_mode,
    form.quick_mode,
  ]);

  const refreshList = async () => { try { setList(await api.backtestList() || []); } catch {} };

  // ---- TF ordering + row accessors for sorting ----
  // Timeframe sort order is intentional, NOT alphabetical. Higher rank
  // = longer TF. Unknown TFs sort last.
  const TF_RANK = {
    "1m": 1, "3m": 3, "5m": 5, "15m": 15, "30m": 30,
    "1h": 60, "2h": 120, "4h": 240, "6h": 360, "8h": 480, "12h": 720,
    "1d": 1440, "3d": 4320, "1w": 10080,
  };
  const tfOf = (b) => b.resolved_timeframe || b.strategy_config_snapshot?.timeframe || b.config?.timeframe || "";

  // ------------------------------------------------------------------
  // Client-side fallback for `config_tags`. Mirrors `_derive_config_tags`
  // in `services/backtest_engine.py`. Used when:
  //   1. The backtest doc was saved BEFORE the engine started writing
  //      `config_tags` (engine version < 2.4.0). Old docs must still
  //      render chips so the operator can tell them apart.
  //   2. The doc is mid-run (no completion-time enrichment yet).
  // Whenever the server-side field is present we use it verbatim — only
  // missing/empty arrays trigger this fallback.
  // ------------------------------------------------------------------
  const _deriveTagsClient = (b) => {
    const cfg = b?.config || {};
    const profile = b?.strategy_config_snapshot || {};
    const tf = b?.resolved_timeframe || profile.timeframe || cfg.timeframe || "";
    const tags = [];
    const add = (key, value, kind = "execution") => {
      if (value === undefined || value === null || value === "") return;
      tags.push({ key, value: String(value), kind });
    };
    add("TF", tf);
    if (cfg.leverage != null) add("Lev", `${cfg.leverage}x`);
    if (cfg.order_size_mode && cfg.order_size_value != null) {
      const v = cfg.order_size_value;
      if (cfg.order_size_mode === "usdt") add("Size", `${v} USDT`);
      else if (cfg.order_size_mode === "percent") add("Size", `${v}%`);
      else add("Size", `${v} (${cfg.order_size_mode})`);
    }
    if (cfg.initial_balance != null) {
      try { add("Bal", `$${Number(cfg.initial_balance).toLocaleString()}`); } catch {}
    }
    if (cfg.fee_rate != null) add("Fee", `${(cfg.fee_rate * 100).toFixed(3)}%`);
    if (cfg.slippage_bps != null) add("Slip", `${cfg.slippage_bps}bp`);
    if (cfg.hedge_mode === true) add("Hedge", "on");
    else if (cfg.hedge_mode === false) add("Hedge", "off");
    // LBank-unrealistic flag (client-side fallback for older docs that
    // were saved before the engine started emitting this chip). Mirrors
    // the same rule used in services/backtest_engine.py:_derive_config_tags.
    // The chip uses a distinct `warning` kind so the chip renderer can
    // render it amber instead of the default neutral.
    {
      let mop = 1;
      try { mop = Number(profile.max_open_positions ?? 1) || 1; } catch {}
      if (cfg.hedge_mode === true && mop >= 2) {
        tags.push({ key: "LBank-unrealistic", value: "hedge+multi", kind: "warning" });
      }
    }
    if (cfg.liquidations_mode) add("Liq", cfg.liquidations_mode);
    const er = profile.exit_rules || {};
    if (er.mode) add("Exit", er.mode, "strategy");
    if (profile.cooldown_minutes != null) add("Cooldown", `${profile.cooldown_minutes}m`, "strategy");
    let maMin = null, alignMax = null, rsiP = null, cvdSm = null;
    for (const c of (profile.conditions || [])) {
      if (c?.enabled === false) continue;
      const p = c?.params || {};
      if (c?.type === "rsi_divergence" || c?.type === "cvd_divergence") {
        if (p.max_alignment_distance != null) {
          const v = Number(p.max_alignment_distance);
          if (alignMax == null || v > alignMax) alignMax = v;
        }
      }
      if (c?.type === "rsi_divergence" && p.rsi_period != null) rsiP = Number(p.rsi_period);
      if (c?.type === "cvd_divergence" && p.cvd_smoothing_length != null) cvdSm = Number(p.cvd_smoothing_length);
      if (c?.type === "ema_alignment") {
        const w = p.fast ?? p.window ?? p.period;
        if (w != null) { const v = Number(w); if (maMin == null || v < maMin) maMin = v; }
      }
    }
    if (maMin != null) add("MA", maMin, "strategy");
    if (alignMax != null) add("Align", alignMax, "strategy");
    if (rsiP != null) add("RSI", rsiP, "strategy");
    if (cvdSm != null && cvdSm > 0) add("CVD-EMA", cvdSm, "strategy");
    return tags;
  };
  const tagsOf = (b) => {
    if (Array.isArray(b?.config_tags) && b.config_tags.length > 0) return b.config_tags;
    return _deriveTagsClient(b);
  };


  // Numeric extractor for each sortable column. Returns `null` for
  // missing values so they can be pushed to the bottom of either sort
  // direction (a row that has never produced metrics shouldn't poison
  // the comparison).
  const sortAccessor = {
    created_at: (b) => (b.created_at ? Date.parse(b.created_at) : null),
    tf: (b) => TF_RANK[tfOf(b)] ?? Number.MAX_SAFE_INTEGER,
    trades: (b) => (b?.metrics?.total_trades ?? null),
    pnl: (b) => (b?.metrics?.total_pnl_usdt ?? null),
    win_rate: (b) => (b?.metrics?.win_rate ?? null),
    max_dd: (b) => (b?.metrics?.max_drawdown_percent ?? null),
  };

  const visibleList = useMemo(() => {
    // 1. Apply exclude-TF filter (non-destructive — UI hide only).
    const exc = new Set(excludeTfs);
    let rows = list.filter((b) => !exc.has(tfOf(b)));
    // 1b. Apply free-text search across config_tags + strategy name +
    //     strategy id. Case-insensitive substring match. Empty search
    //     skips the filter entirely.
    const q = (searchText || "").trim().toLowerCase();
    if (q) {
      rows = rows.filter((b) => {
        const haystack = [
          b?.strategy_config_snapshot?.name || "",
          b?.config?.strategy_id || "",
          b?.id || "",
          b?.config?.research_preset_label || "",
          b?.config?.research_regime_type || "",
          ...tagsOf(b).map((t) => `${t.key}=${t.value}`),
        ].join(" ").toLowerCase();
        return haystack.includes(q);
      });
    }
    // 1c. Apply regime filter. "all" passes everything. Any other
    // value matches rows where `config.research_regime_type` is
    // exactly that value. Rows WITHOUT the field NEVER match a
    // specific regime — they only appear under "all".
    if (regimeFilter && regimeFilter !== "all") {
      rows = rows.filter((b) =>
        (b?.config?.research_regime_type || "") === regimeFilter,
      );
    }
    // 2. Sort. Missing values always go to the BOTTOM regardless of
    //    direction so a column of mostly-completed rows isn't dominated
    //    by failed/cancelled rows that have no metrics.
    const acc = sortAccessor[sortKey] || sortAccessor.created_at;
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      const aMissing = va === null || va === undefined || Number.isNaN(va);
      const bMissing = vb === null || vb === undefined || Number.isNaN(vb);
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;   // bottom
      if (bMissing) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, excludeTfs, sortKey, sortDir, searchText, regimeFilter]);

  const toggleSort = (key) => {
    if (sortKey !== key) {
      // First click on a NEW column → ascending (per spec).
      setSortKey(key);
      setSortDir("asc");
    } else {
      // Second click on the SAME column → flip direction.
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    }
  };

  // Distinct TFs present in the current list — used to populate the
  // bulk-delete and exclude-TF multi-selects.
  const availableTfs = (() => {
    const seen = new Set();
    list.forEach((b) => { const t = tfOf(b); if (t) seen.add(t); });
    return Array.from(seen).sort((a, b) => (TF_RANK[a] ?? 9e9) - (TF_RANK[b] ?? 9e9));
  })();

  const previewBulkDelete = async () => {
    if (bulkDeleteTfs.length === 0) {
      toast.error("Select at least one timeframe to delete.");
      return;
    }
    setBulkBusy(true);
    try {
      const out = await api.backtestsBulkPreview({ timeframes: bulkDeleteTfs });
      if ((out.matching_count || 0) === 0) {
        toast.info("No backtests match the selected timeframes.");
        setBulkBusy(false);
        return;
      }
      setBulkDeletePreview(out);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBulkBusy(false); }
  };

  const confirmBulkDelete = async () => {
    if (!bulkDeletePreview) return;
    setBulkBusy(true);
    try {
      const out = await api.backtestsBulkDelete({ timeframes: bulkDeleteTfs });
      toast.success(`Deleted ${out.deleted_count} backtest(s).`);
      setBulkDeletePreview(null);
      setBulkDeleteTfs([]);
      await refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBulkBusy(false); }
  };

  // Load profiles for summary card
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.strategyProfiles();
        const active = await api.strategyActive();
        if (!alive) return;
        setProfiles(list || []);
        setActiveId(active?.active_strategy_id || null);
        // Priority: URL param > localStorage-persisted last selection >
        //   already-picked in this session > active strategy.
        // We only adopt a persisted id if the profile still exists in the
        // returned list — otherwise we clear the LS key and fall through to
        // the next priority tier (so a deleted profile never sticks around).
        const allIds = new Set((list || []).map((p) => p.id));
        const incoming = urlStrategyId && allIds.has(urlStrategyId) ? urlStrategyId : null;
        const persisted = loadPersistedProfileId();
        const persistedValid = persisted && allIds.has(persisted) ? persisted : null;
        if (persisted && !persistedValid) persistProfileId(null);  // clear stale LS
        if (incoming) {
          setPicker({ mode: "profile", profileId: incoming });
        } else if (!picker.profileId && persistedValid) {
          setPicker({ mode: "profile", profileId: persistedValid });
        } else if (!picker.profileId && active?.active_strategy_id) {
          setPicker({ mode: "profile", profileId: active.active_strategy_id });
        }
      } catch {}
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStrategyId]);

  // Keep ?strategy_id= AND localStorage in sync with the picker so the
  // URL is shareable AND the page restores the last selection on return.
  // We deliberately ONLY persist non-null ids and never clear from this
  // effect — clearing is the bootstrap effect's responsibility (it knows
  // when an id is stale because the profile was deleted). Otherwise the
  // initial-mount fire (picker.profileId === null before the async load)
  // would wipe the saved id before we even get a chance to restore it.
  useEffect(() => {
    if (picker.profileId && picker.profileId !== urlStrategyId) {
      setSearchParams({ strategy_id: picker.profileId }, { replace: true });
    }
    if (picker.profileId) {
      persistProfileId(picker.profileId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.profileId]);

  useEffect(() => { refreshList(); }, []);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === (picker.profileId || activeId)),
    [profiles, picker, activeId],
  );

  // Temporary helper: snapshot the active profile and overlay the
  // experimental regime_direction_gate. Returns null when no profile
  // is selected (caller falls back to the existing strategy_id path).
  // The backend Pydantic schema accepts the partial gate dict — server
  // fills missing keys with the centralised defaults (see
  // services/regime_classifier.DEFAULTS).
  const buildRegimeGatedInlineProfile = (base) => {
    const src = base || selectedProfile;
    if (!src) return null;
    return {
      ...src,
      regime_direction_gate: {
        enabled: !!regimeGate.enabled,
        regime_timeframe: regimeGate.regime_timeframe || "2h",
      },
    };
  };

  // poll active backtest
  useEffect(() => {
    if (!running?.id) return;
    let id; let stopped = false;
    const tick = async () => {
      try {
        const d = await api.backtestGet(running.id, true);
        if (stopped) return;
        setDoc(d);
        if (["completed", "cancelled", "failed"].includes(d.status)) {
          setRunning(null);
          refreshList();
        }
      } catch {}
    };
    tick();
    id = setInterval(tick, 1200);
    return () => { stopped = true; clearInterval(id); };
  }, [running]);

  // Composite-backtest polling — slow tick (10s) since each window is
  // a full child backtest (typically minutes each).
  useEffect(() => {
    if (!compositeRunning?.id) return;
    let id; let stopped = false;
    const tick = async () => {
      try {
        const d = await api.backtestCompositeGet(compositeRunning.id);
        if (stopped) return;
        setCompositeDoc(d);
        if (["completed", "failed", "cancelled"].includes(d.status)) {
          setCompositeRunning(null);
        }
      } catch {}
    };
    tick();
    id = setInterval(tick, 10_000);
    return () => { stopped = true; clearInterval(id); };
  }, [compositeRunning]);

  // Date-range validity, computed once per render. Used to gate the Run
  // button and surface an inline error so the user can't submit a clearly
  // broken request (e.g. end < start). Server still validates authoritatively.
  const dateError = useMemo(() => {
    if (!form.start_iso || !form.end_iso) return "Both start and end dates are required.";
    const s = new Date(form.start_iso);
    const e = new Date(form.end_iso);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "Invalid date(s).";
    if (e <= s) return "End date must be strictly after start date.";
    const diffDays = (e - s) / 86_400_000;
    if (diffDays > 366) return `Range exceeds 366 days (got ${diffDays.toFixed(0)}d). Server max is 366d.`;
    return null;
  }, [form.start_iso, form.end_iso]);

  const submit = async () => {
    if (!picker.profileId) {
      toast.error("Pick a strategy profile first");
      return;
    }
    // Composite preset → fan out to N independent backtests via the
    // composite endpoint instead of /backtest/run. Date inputs are
    // ignored in this branch — the windows come from the preset.
    const _composite = findPreset(researchPresetId);
    if (_composite && _composite.type === "composite") {
      try {
        const body = {
          symbol: form.symbol,
          initial_balance: parseLocaleFloat(form.initial_balance),
          leverage: parseLocaleInt(form.leverage),
          order_size_mode: form.order_size_mode,
          order_size_value: parseLocaleFloat(form.order_size_value),
          hedge_mode: form.hedge_mode,
          max_open_long_positions: parseLocaleInt(form.max_open_long_positions) || 1,
          max_open_short_positions: parseLocaleInt(form.max_open_short_positions) || 1,
          min_time_between_trades_seconds: parseLocaleInt(form.min_time_between_trades_seconds) || 0,
          min_price_move_between_same_side_entries_pct: parseLocaleFloat(form.min_price_move_between_same_side_entries_pct) || 0,
          fee_rate: parseLocaleFloat(form.fee_rate),
          slippage_bps: parseLocaleFloat(form.slippage_bps),
          liquidations_mode: form.liquidations_mode,
          quick_mode: !!form.quick_mode,
          strategy_id: picker.profileId,
          research_preset_id:    _composite.id,
          research_preset_label: _composite.label,
          research_regime_type:  _composite.regime_type,
          windows: _composite.windows.map((w) => ({
            start_iso: new Date(w.start_iso).toISOString(),
            end_iso:   new Date(w.end_iso).toISOString(),
          })),
        };
        if (inlineProfileOverride) body.inline_profile = inlineProfileOverride;
        // Temporary regime gate wiring (composite path).
        if (regimeGate.enabled) {
          const gated = buildRegimeGatedInlineProfile(inlineProfileOverride);
          if (gated) body.inline_profile = gated;
        }
        // Backend `BacktestLaunchRequest` validator rejects requests
        // that carry BOTH `strategy_id` AND `inline_profile` (commit
        // b290f0f, 2026-05-01). When we send an inline_profile snapshot
        // we MUST drop the strategy_id key — keeping both would 422.
        if (body.inline_profile) delete body.strategy_id;
        const r = await api.backtestCompositeRun(body);
        setCompositeRunning({ id: r.id });
        setCompositeDoc(null);
        setInlineProfileOverride(null);
        setOverrideLabel("");
        toast.success(`Composite backtest queued: ${_composite.label} (${r.total_windows} windows)`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      }
      return;
    }
    if (dateError) {
      toast.error(dateError);
      return;
    }
    try {
      const body = {
        symbol: form.symbol,
        start_iso: new Date(form.start_iso).toISOString(),
        end_iso: new Date(form.end_iso).toISOString(),
        initial_balance: parseLocaleFloat(form.initial_balance),
        leverage: parseLocaleInt(form.leverage),
        order_size_mode: form.order_size_mode,
        order_size_value: parseLocaleFloat(form.order_size_value),
        hedge_mode: form.hedge_mode,
        max_open_long_positions: parseLocaleInt(form.max_open_long_positions) || 1,
        max_open_short_positions: parseLocaleInt(form.max_open_short_positions) || 1,
        min_time_between_trades_seconds: parseLocaleInt(form.min_time_between_trades_seconds) || 0,
        // Locale-safe parse — pt-BR users typing `0,8` previously
        // had it silently coerced to 0 by `parseFloat`, disabling
        // this filter. Now correctly normalised to 0.8.
        min_price_move_between_same_side_entries_pct: parseLocaleFloat(form.min_price_move_between_same_side_entries_pct) || 0,
        fee_rate: parseLocaleFloat(form.fee_rate),
        slippage_bps: parseLocaleFloat(form.slippage_bps),
        liquidations_mode: form.liquidations_mode,
        active_preset: activePresetKey,
        strategy_id: picker.profileId,
        quick_mode: !!form.quick_mode,
        // Research Window preset metadata (when a regime preset is
        // selected). Persisted on the backtest doc so the history
        // table can tag and filter rows by market regime.
        ...(() => {
          const p = findPreset(researchPresetId);
          if (!p || p.id === "custom") return {};
          // `preset_id` / `preset_name` / `preset_expected_label`
          // are consumed by the backtest engine to emit diagnostic
          // fields (regime_source = "preset_precomputed" | "auto_classifier",
          // classifier_signature, preset_computed_distribution) so
          // the operator can see how the REAL classifier read this
          // window vs what the preset claims. NEVER used to force
          // the regime label.
          return {
            research_preset_id:    p.id,
            research_preset_label: p.label,
            research_regime_type:  p.regime_type,
            preset_id:             p.id,
            preset_name:           p.label,
            preset_expected_label: p.regime_type,
          };
        })(),
      };
      // When an inline profile snapshot is primed (Rerun / Duplicate),
      // pass it through. The backend (`server.py:1731 BacktestRunRequest`)
      // accepts `inline_profile` and uses it verbatim instead of looking
      // up the live profile by `strategy_id`. This is the immutability
      // guarantee — editing the live strategy after the original run
      // does NOT affect the rerun.
      if (inlineProfileOverride) {
        body.inline_profile = inlineProfileOverride;
        // strategy_id is mutually exclusive with inline_profile on
        // the backend (BacktestLaunchRequest validator) — see the
        // composite path above. Keep only inline_profile when both
        // would otherwise be set.
      }
      // Temporary regime gate wiring (single-window path). Layers on
      // top of any existing inline override so Rerun + regime toggle
      // compose correctly.
      if (regimeGate.enabled) {
        const gated = buildRegimeGatedInlineProfile(inlineProfileOverride);
        if (gated) body.inline_profile = gated;
      }
      // Mutual-exclusivity contract — see server.py:1932.
      if (body.inline_profile) delete body.strategy_id;
      const r = await api.backtestRun(body);
      setRunning({ id: r.id });
      setDoc(null);
      // Once submitted, clear the override so subsequent vanilla runs
      // go through the live profile lookup as usual.
      setInlineProfileOverride(null);
      setOverrideLabel("");
      toast.success(`Backtest queued (${r.id.slice(0, 8)})`);
    } catch (e) {
      // Pydantic 422 returns `detail` as an array of validation-error
      // dicts; coerce that into a readable string so the toast doesn't
      // show "[object Object]". Pure UX — does not change error
      // handling semantics.
      const det = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(det)) {
        msg = det.map((d) => d?.msg || JSON.stringify(d)).join("; ");
      } else if (typeof det === "string") {
        msg = det;
      } else {
        msg = e?.message || "Backtest launch failed";
      }
      toast.error(msg);
    }
  };

  const cancel = async () => {
    if (!running?.id) return;
    try { await api.backtestCancel(running.id); toast("Cancel requested"); }
    catch (e) { toast.error(e.message); }
  };

  const open = async (id) => {
    try { setDoc(await api.backtestGet(id, true)); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    try { await api.backtestDelete(id); refreshList(); if (doc?.id === id) setDoc(null); }
    catch (e) { toast.error(e.message); }
  };

  // Cleanup step 1 — dry-run preview. Opens the confirmation dialog.
  const requestCleanup = async () => {
    const pf = parseFloat(cleanupPf);
    if (!Number.isFinite(pf) || pf <= 0 || pf > 10_000) {
      toast.error("Profit factor threshold must be a positive number (0 < PF ≤ 10000).");
      return;
    }
    setCleanupBusy(true);
    try {
      const out = await api.backtestCleanupPreview(pf);
      setCleanupPreview(out);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  // Cleanup step 2 — user explicitly confirmed. Destructive.
  const confirmCleanup = async () => {
    if (!cleanupPreview) return;
    setCleanupBusy(true);
    try {
      const out = await api.backtestCleanup(cleanupPreview.threshold);
      toast.success(`Deleted ${out.deleted_count} backtest${out.deleted_count === 1 ? "" : "s"} below PF ${out.threshold}.`);
      setCleanupPreview(null);
      refreshList();
    } catch (e) {
      toast.error(e.message || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const applyPreset = (days, key) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const next_start = isoToInput(start.toISOString());
    const next_end = isoToInput(end.toISOString());
    setForm((f) => ({ ...f, start_iso: next_start, end_iso: next_end }));
    if (key) setActivePresetKey(key);
    persistRange({ start_iso: next_start, end_iso: next_end, activePresetKey: key });
  };

  // Pre-fill the form with a completed backtest's EXACT config + strategy
  // snapshot, then SCROLL TO TOP so the operator can review/edit before
  // hitting Run. Does NOT launch the run automatically — that's
  // intentional: operators frequently want to tweak one knob (e.g.
  // bump leverage from 5x to 10x while keeping everything else
  // identical).
  //
  // Critical immutability behavior: the strategy is primed via
  // `inlineProfileOverride` (a deep copy of `strategy_config_snapshot`).
  // When the operator clicks Run, the body carries `inline_profile`
  // and the server uses it VERBATIM, bypassing the live-profile lookup.
  // So if the original strategy was edited since the run, the rerun
  // STILL uses the original snapshot's conditions/params.
  //
  // `kind` is purely cosmetic — affects the toast label. Both Rerun
  // and Duplicate share identical pre-fill logic.
  const _prefillFromDoc = (sourceDoc, kind = "rerun") => {
    const cfg = sourceDoc?.config || {};
    const snap = sourceDoc?.strategy_config_snapshot;
    if (!cfg || Object.keys(cfg).length === 0) {
      toast.error("This run has no saved config — cannot pre-fill.");
      return;
    }
    // Lock the four risk-mirror fields against the (possibly still
    // in-flight) Risk-Settings prefill effect. Snapshot values must
    // win on Rerun/Duplicate even if the network resolves late.
    for (const k of RISK_PREFILL_KEYS) {
      if (cfg[k] !== undefined && cfg[k] !== null) {
        userTouchedRef.current.add(k);
      }
    }
    setPrefilledFromRisk({
      max_open_long_positions: false,
      max_open_short_positions: false,
      min_time_between_trades_seconds: false,
      min_price_move_between_same_side_entries_pct: false,
    });
    // Patch the form. Date inputs need the local-input format, not the
    // raw ISO Z form — reuse the same `isoToInput` helper used by date
    // pickers below. (When start_iso/end_iso are in input-format already
    // — e.g. localStorage round-trip — `isoToInput` is idempotent.)
    setForm((f) => ({
      ...f,
      symbol: cfg.symbol || f.symbol,
      start_iso: cfg.start_iso ? isoToInput(cfg.start_iso) : f.start_iso,
      end_iso: cfg.end_iso ? isoToInput(cfg.end_iso) : f.end_iso,
      initial_balance: cfg.initial_balance ?? f.initial_balance,
      leverage: cfg.leverage ?? f.leverage,
      order_size_mode: cfg.order_size_mode ?? f.order_size_mode,
      order_size_value: cfg.order_size_value ?? f.order_size_value,
      hedge_mode: typeof cfg.hedge_mode === "boolean" ? cfg.hedge_mode : f.hedge_mode,
      max_open_long_positions: cfg.max_open_long_positions ?? f.max_open_long_positions,
      max_open_short_positions: cfg.max_open_short_positions ?? f.max_open_short_positions,
      min_time_between_trades_seconds: cfg.min_time_between_trades_seconds ?? f.min_time_between_trades_seconds,
      min_price_move_between_same_side_entries_pct: cfg.min_price_move_between_same_side_entries_pct ?? f.min_price_move_between_same_side_entries_pct,
      fee_rate: cfg.fee_rate ?? f.fee_rate,
      slippage_bps: cfg.slippage_bps ?? f.slippage_bps,
      liquidations_mode: cfg.liquidations_mode ?? f.liquidations_mode,
    }));
    if (cfg.active_preset) setActivePresetKey(cfg.active_preset);
    // Wire the strategy snapshot. We deep-clone defensively so any
    // future mutation of `sourceDoc` doesn't ripple.
    if (snap && Object.keys(snap).length > 0) {
      let cloned = snap;
      try { cloned = JSON.parse(JSON.stringify(snap)); } catch {}
      setInlineProfileOverride(cloned);
      setOverrideLabel(snap.name || "snapshot");
    } else {
      setInlineProfileOverride(null);
      setOverrideLabel("");
    }
    // Hop the picker to a faux "snapshot" mode — keep the existing
    // profileId for cross-reference but the server will prefer
    // `inline_profile` once present.
    if (snap?.id) setPicker({ mode: "profile", profileId: snap.id });
    // UX: clear current result view so the operator sees the form,
    // then nudge scroll position so the form is visible.
    setDoc(null);
    if (typeof window !== "undefined") {
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
    }
    const verb = kind === "duplicate" ? "Duplicated" : "Pre-filled for re-run";
    const idTail = (sourceDoc?.id || "").slice(0, 8);
    toast.success(`${verb} from ${idTail}. Review the form, then click Run.`);
  };
  const rerunFromDoc = (sourceDoc) => _prefillFromDoc(sourceDoc, "rerun");
  const duplicateFromDoc = (sourceDoc) => _prefillFromDoc(sourceDoc, "duplicate");

  return (
    <div className="p-6 space-y-6">
      <SectionTitle sub="Replays historical Binance Futures klines through the SAME composable Strategy Profile evaluator the live engine uses. Entries fill at next-candle open (no look-ahead). Per-strategy exit rules and cooldown apply.">
        Strategy Backtest
      </SectionTitle>

      {/* Strategy picker + summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-[#0F0F14] border border-border rounded-sm p-4">
          <StrategyPicker value={picker} onChange={setPicker} testidPrefix="bt-strat" />
        </div>
        <div className="lg:col-span-2">
          <StrategySummary profile={selectedProfile} />
        </div>
      </div>

      {/* Config card (timeframe is OWNED by the strategy profile) */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        {/* Research Window preset selector — operator-curated BTC
            market regimes. Single-window presets fill start/end and
            leave manual editing intact. Composite presets fan out to
            N independent backtests via /backtest/composite/run. */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-[#71717A]">
            Research window:
          </span>
          <Select
            value={researchPresetId}
            onValueChange={(v) => {
              setResearchPresetId(v);
              persistResearchPreset("backtest", v);
              const p = findPreset(v);
              if (!p || p.type !== "single" || !p.start_iso || !p.end_iso) {
                // composite / custom → leave existing dates intact
                if (p && p.type === "composite") {
                  toast(`Selected ${p.label}: ${p.windows.length} non-contiguous windows. Click Run to fan out.`);
                }
                return;
              }
              const next_start = isoToInput(p.start_iso);
              const next_end   = isoToInput(p.end_iso);
              setForm((f) => ({ ...f, start_iso: next_start, end_iso: next_end }));
              setActivePresetKey("custom");
              persistRange({ start_iso: next_start, end_iso: next_end, activePresetKey: "custom" });
            }}
          >
            <SelectTrigger
              className="w-72 bg-[#1A1A22] border-border rounded-sm h-7 text-xs"
              data-testid="bt-research-window-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESEARCH_PRESETS.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  data-testid={`bt-research-window-option-${p.id}`}
                >
                  {p.label}
                  {p.type === "composite" ? "  (composite)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(() => {
            const p = findPreset(researchPresetId);
            if (!p || p.type !== "composite") return null;
            return (
              <span
                data-testid="bt-research-window-composite-hint"
                className="text-[11px] text-[#F59E0B]"
              >
                Composite: {p.windows.length} non-contiguous windows · date fields below are ignored on run
              </span>
            );
          })()}
          <ResearchCachePill
            presetId={researchPresetId}
            strategyProfileId={picker.profileId}
            symbol={form.symbol}
            testidPrefix="bt"
          />
          <WarmAllPresetsButton
            strategyProfileId={picker.profileId}
            symbol={form.symbol}
            testidPrefix="bt"
          />
          <CacheTelemetryBadge testidPrefix="bt" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RegimePresetCachePanel
            symbol={form.symbol}
            preset={findPreset(researchPresetId)}
            regimeGate={regimeGate}
            testidPrefix="bt"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-[#71717A] mr-1">Quick range:</span>
          {DATE_PRESETS.map((p) => {
            const isActive = activePresetKey === p.key;
            return (
              <Button
                key={p.key}
                size="sm"
                variant="outline"
                onClick={() => applyPreset(p.days, p.key)}
                data-testid={`bt-preset-${p.key}`}
                data-active={isActive ? "true" : "false"}
                aria-pressed={isActive}
                className={
                  "rounded-sm text-xs h-7 transition-all duration-150 " +
                  (isActive
                    ? "bg-[#00E676]/15 border-[#00E676] text-[#00E676] ring-1 ring-[#00E676]/70 shadow-[0_0_10px_-2px_rgba(0,230,118,0.55)] hover:bg-[#00E676]/20"
                    : "border-border bg-[#1A1A22] hover:bg-[#262633] text-[#A0A0AB]")
                }
              >
                {p.label}
              </Button>
            );
          })}
          {activePresetKey === "custom" ? (
            <span
              data-testid="bt-preset-custom"
              data-active="true"
              className="inline-flex items-center px-2 h-7 rounded-sm text-xs bg-[#3B82F6]/15 border border-[#3B82F6] text-[#3B82F6] ring-1 ring-[#3B82F6]/70 shadow-[0_0_10px_-2px_rgba(59,130,246,0.55)] transition-all duration-150"
            >
              Custom range
            </span>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="md:col-span-3 lg:col-span-4 -mb-2 flex items-center justify-end">
          <Button
            data-testid="bt-sync-risk"
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs rounded-sm border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10"
            onClick={async () => {
              for (const k of RISK_PREFILL_KEYS) userTouchedRef.current.delete(k);
              await _applyRiskPrefill(true);
              toast.success("Synced stacking/risk fields from current Risk Settings.");
            }}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Sync from Risk Settings
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Symbol</Label>
          <Input value={form.symbol} disabled className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Timeframe</Label>
          <Input
            value={selectedProfile?.timeframe || "—"}
            disabled
            className="bg-[#1A1A22] border-border rounded-sm font-mono text-[#A0A0AB]"
            data-testid="bt-tf-display"
          />
          <span className="text-[10px] text-[#71717A]">From profile</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Start (UTC)</Label>
          <Input data-testid="bt-start" type="datetime-local" value={form.start_iso} onChange={(e) => { const v = e.target.value; setForm({ ...form, start_iso: v }); setActivePresetKey("custom"); persistRange({ start_iso: v, end_iso: form.end_iso, activePresetKey: "custom" }); }} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">End (UTC)</Label>
          <Input data-testid="bt-end" type="datetime-local" value={form.end_iso} onChange={(e) => { const v = e.target.value; setForm({ ...form, end_iso: v }); setActivePresetKey("custom"); persistRange({ start_iso: form.start_iso, end_iso: v, activePresetKey: "custom" }); }} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Initial balance (USDT)</Label>
          <Input data-testid="bt-balance" type="number" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Leverage</Label>
          <Input data-testid="bt-leverage" type="number" value={form.leverage} onChange={(e) => setForm({ ...form, leverage: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Order size mode</Label>
          <Select value={form.order_size_mode} onValueChange={(v) => setForm({ ...form, order_size_mode: v })}>
            <SelectTrigger className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="usdt">Fixed USDT</SelectItem><SelectItem value="percent">% of balance</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Order size value</Label>
          <Input data-testid="bt-size" type="number" value={form.order_size_value} onChange={(e) => setForm({ ...form, order_size_value: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            Max open LONG positions
            {prefilledFromRisk.max_open_long_positions && (
              <span data-testid="bt-prefilled-max-long" className="text-[10px] text-[#86EFAC] ml-2">
                (prefilled from Risk Settings)
              </span>
            )}
          </Label>
          <Input
            data-testid="bt-max-long"
            type="number"
            min="1"
            max="20"
            value={form.max_open_long_positions}
            onChange={(e) => {
              markUserTouched("max_open_long_positions");
              setForm({ ...form, max_open_long_positions: e.target.value });
            }}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            Max open SHORT positions
            {prefilledFromRisk.max_open_short_positions && (
              <span data-testid="bt-prefilled-max-short" className="text-[10px] text-[#86EFAC] ml-2">
                (prefilled from Risk Settings)
              </span>
            )}
          </Label>
          <Input
            data-testid="bt-max-short"
            type="number"
            min="1"
            max="20"
            value={form.max_open_short_positions}
            onChange={(e) => {
              markUserTouched("max_open_short_positions");
              setForm({ ...form, max_open_short_positions: e.target.value });
            }}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            Min time between trades (sec) <span className="text-[10px] text-[#71717A]">— mirrors risk_settings; 0 disables</span>
            {prefilledFromRisk.min_time_between_trades_seconds && (
              <span data-testid="bt-prefilled-min-time" className="text-[10px] text-[#86EFAC] ml-2">
                (prefilled from Risk Settings)
              </span>
            )}
          </Label>
          <Input
            data-testid="bt-min-time"
            type="number"
            min="0"
            max="86400"
            value={form.min_time_between_trades_seconds}
            onChange={(e) => {
              markUserTouched("min_time_between_trades_seconds");
              setForm({ ...form, min_time_between_trades_seconds: e.target.value });
            }}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            Min price move between same-side entries (%) <span className="text-[10px] text-[#71717A]">— 0 disables</span>
            {prefilledFromRisk.min_price_move_between_same_side_entries_pct && (
              <span data-testid="bt-prefilled-min-pricemove" className="text-[10px] text-[#86EFAC] ml-2">
                (prefilled from Risk Settings)
              </span>
            )}
          </Label>
          <Input
            data-testid="bt-min-pricemove"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.min_price_move_between_same_side_entries_pct}
            onChange={(e) => {
              markUserTouched("min_price_move_between_same_side_entries_pct");
              setForm({ ...form, min_price_move_between_same_side_entries_pct: e.target.value });
            }}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
          {(() => {
            // Live parse echo — surfaces locale-aware interpretation
            // so users typing a comma decimal can see whether the
            // value will reach the engine as the intended threshold.
            const parsed = parseLocaleFloat(form.min_price_move_between_same_side_entries_pct);
            const raw = String(form.min_price_move_between_same_side_entries_pct ?? "");
            if (raw && Number.isFinite(parsed)) {
              return (
                <div data-testid="bt-min-pricemove-echo" className="text-[10px] text-[#86EFAC] font-mono">
                  → {parsed}% used by engine
                </div>
              );
            }
            if (raw && !Number.isFinite(parsed)) {
              return (
                <div className="text-[10px] text-[#F59E0B] font-mono">
                  → invalid number; engine will treat as 0
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Fee rate (per side, e.g. 0.0005 = 0.05%)</Label>
          <Input
            data-testid="bt-fee-rate"
            type="number"
            step="0.0001"
            min="0"
            max="0.01"
            value={form.fee_rate}
            onChange={(e) => {
              const v = e.target.value;
              setForm({ ...form, fee_rate: v });
              persistFeeRate(v);
            }}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Slippage (bps)</Label>
          <Input type="number" value={form.slippage_bps} onChange={(e) => setForm({ ...form, slippage_bps: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Liquidation simulation</Label>
          <Select value={form.liquidations_mode} onValueChange={(v) => setForm({ ...form, liquidations_mode: v })}>
            <SelectTrigger data-testid="bt-liq-mode" className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="skip">Skip (condition ignored)</SelectItem>
              <SelectItem value="historical">Historical (real Coinalyze data)</SelectItem>
              <SelectItem value="assume_pass">Assume PASS (over-optimistic)</SelectItem>
              <SelectItem value="assume_fail">Assume FAIL (never triggers)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-3 lg:col-span-4 flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-3 flex-wrap">
            <Switch data-testid="bt-hedge" checked={form.hedge_mode} onCheckedChange={(v) => setForm({ ...form, hedge_mode: v })} />
            <Label className="text-xs">Hedge mode</Label>
            {form.hedge_mode ? (
              <span
                data-testid="bt-hedge-lbank-warning"
                className="text-[10px] text-[#F59E0B] font-mono leading-tight max-w-[460px]"
              >
                Hedge mode is not supported on LBank one-way mode. Results
                with Hedge ON and max_open_positions ≥ 2 may not be
                replayable live.
              </span>
            ) : null}
            <span className="mx-2 text-border">·</span>
            <Switch
              data-testid="bt-quick-mode"
              checked={!!form.quick_mode}
              onCheckedChange={(v) => setForm({ ...form, quick_mode: v })}
            />
            <Label className="text-xs" htmlFor="bt-quick-mode" title="Skips MFE/MAE, breakeven sims, duration analytics. Core metrics identical.">
              Quick Mode
            </Label>
            <span
              data-testid="bt-quick-mode-help"
              className="text-[10px] text-muted-foreground font-mono leading-tight max-w-[380px]"
            >
              Faster, skips advanced analytics. Use for parameter search and
              optimization. Disable for final deep analysis.
            </span>
            {/* TEMPORARY experimental regime-gate toggle (P2-B/C UI hook).
                Minimal exposure: switch + tf picker. No styling polish —
                will be replaced by P2-E when the full editor section ships. */}
            <span className="mx-2 text-border">·</span>
            <Switch
              data-testid="bt-regime-gate-toggle"
              checked={!!regimeGate.enabled}
              onCheckedChange={(v) => setRegimeGate((g) => ({ ...g, enabled: v }))}
            />
            <Label
              className="text-xs"
              title="Experimental: gates ENTRIES only by EMA+ADX regime on regime_timeframe. Exits unaffected."
            >
              Regime Gate
            </Label>
            <select
              data-testid="bt-regime-gate-tf"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={regimeGate.regime_timeframe}
              disabled={!regimeGate.enabled}
              onChange={(e) => setRegimeGate((g) => ({ ...g, regime_timeframe: e.target.value }))}
            >
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h (baseline)</option>
            </select>
            {regimeGate.enabled ? (
              <span
                data-testid="bt-regime-gate-help"
                className="text-[10px] text-[#10B981] font-mono leading-tight"
              >
                gate ON · tf={regimeGate.regime_timeframe}
              </span>
            ) : null}
            {dateError ? (
              <span data-testid="bt-date-error" className="text-xs text-[#FF3B30]">
                {dateError}
              </span>
            ) : null}
            {(() => {
              // Pre-flight heaviness estimator. Surfaces a warning when
              // the (timeframe × date range × profile complexity) tuple
              // is likely to produce a heavy result. Pure derived state
              // — does NOT block the run. Encourages Quick Mode on for
              // very large configs.
              if (!form.start_iso || !form.end_iso) return null;
              const startMs = new Date(form.start_iso).getTime();
              const endMs = new Date(form.end_iso).getTime();
              if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
              const days = (endMs - startMs) / 86_400_000;
              const tfMinutes = ({ "1m": 1, "3m": 3, "5m": 5, "15m": 15, "30m": 30, "1h": 60 }[form.timeframe || "5m"]) || 5;
              const candleCount = Math.round((days * 1440) / tfMinutes);
              // Profile complexity proxy: count enabled conditions across
              // flat + groups. Doesn't peek into picker internals.
              const prof = picker?.profile;
              let condCount = 0;
              if (prof) {
                const flat = (prof.conditions || []).filter((c) => c.enabled !== false).length;
                const grouped = (prof.condition_groups || [])
                  .flatMap((g) => g.conditions || [])
                  .filter((c) => c.enabled !== false).length;
                condCount = flat + grouped;
              }
              // Heuristic threshold: 1m × 30+ days OR ≥3 enabled conditions
              // at 1m × 7+ days OR any 1m × 90+ days.
              const heavy = (
                (tfMinutes === 1 && days >= 90) ||
                (tfMinutes === 1 && days >= 30 && condCount >= 2) ||
                (tfMinutes <= 3 && days >= 14 && condCount >= 3) ||
                (candleCount > 50_000)
              );
              if (!heavy) return null;
              const stackingOn = (Number(form.max_open_long_positions) > 1)
                || (Number(form.max_open_short_positions) > 1);
              return (
                <span
                  data-testid="bt-heavy-warning"
                  className="text-[10px] text-[#F59E0B] font-mono leading-tight max-w-[520px]"
                >
                  Heavy config (~{candleCount.toLocaleString()} candles
                  {condCount ? `, ${condCount} conditions` : ""}
                  {stackingOn ? ", stacking on" : ""}). Consider enabling
                  Quick Mode to keep the result page responsive.
                </span>
              );
            })()}
          </div>
          <div className="flex gap-2">
            {running ? (
              <Button data-testid="bt-cancel" variant="outline" onClick={cancel} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
                <X size={14} strokeWidth={1.5} className="mr-1.5" /> Cancel
              </Button>
            ) : (
              <Button
                data-testid="bt-run"
                onClick={submit}
                disabled={
                  !picker.profileId ||
                  (findPreset(researchPresetId)?.type !== "composite" && !!dateError)
                }
                className="bg-[#00E676] text-black hover:bg-[#00C865] rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Play size={14} strokeWidth={2} className="mr-1.5" />
                {findPreset(researchPresetId)?.type === "composite"
                  ? `Run Composite (${findPreset(researchPresetId).windows.length} windows)`
                  : "Run Backtest"}
              </Button>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Composite backtest progress + results card */}
      {compositeRunning || compositeDoc ? (
        <div
          className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-3"
          data-testid="bt-composite-card"
        >
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#A0A0AB]">
              Composite backtest{" "}
              <span className="font-mono text-white">
                {(compositeRunning?.id || compositeDoc?.id || "").slice(0, 8)}…
              </span>
              {compositeDoc?.preset_label
                ? <span className="ml-2 text-[#F59E0B]">· {compositeDoc.preset_label}</span>
                : null}
            </span>
            <span className="font-mono text-xs text-[#71717A]">
              {compositeDoc?.completed_windows || 0} / {compositeDoc?.total_windows || "—"} windows
            </span>
          </div>
          <Progress
            value={
              compositeDoc?.total_windows
                ? (compositeDoc.completed_windows / compositeDoc.total_windows) * 100.0
                : 0
            }
            className="h-1.5"
          />
          <div className="text-xs text-[#71717A]">
            Status: {compositeDoc?.status || "pending"}
            {compositeDoc?.status === "running" && compositeDoc?.current_window
              ? ` · ${compositeDoc.current_window.start_iso?.slice(0, 10)} → ${compositeDoc.current_window.end_iso?.slice(0, 10)}`
              : null}
            {compositeDoc?.error ? ` · ${compositeDoc.error}` : null}
          </div>
          {compositeDoc?.composite_summary ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3" data-testid="bt-composite-summary">
              <StatCard label="Total Trades" value={String(compositeDoc.composite_summary.total_trades ?? "—")} />
              <StatCard label="Total PnL" value={fmtUsd(compositeDoc.composite_summary.total_pnl_usdt)} />
              <StatCard label="Win Rate" value={fmtPct(compositeDoc.composite_summary.win_rate)} />
              <StatCard label="Profit Factor" value={(compositeDoc.composite_summary.profit_factor ?? 0).toFixed(2)} />
              <StatCard label="Worst Window DD" value={fmtPct(compositeDoc.composite_summary.max_drawdown_percent)} />
              <StatCard label="Windows Completed" value={String(compositeDoc.composite_summary.windows_completed ?? "—")} />
              <StatCard label="Windows Failed" value={String(compositeDoc.composite_summary.windows_failed ?? "—")} />
            </div>
          ) : null}
          {compositeDoc?.child_results?.length ? (
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Per-window</div>
              <table className="w-full text-xs" data-testid="bt-composite-children-table">
                <thead className="text-[#71717A]">
                  <tr>
                    <th className="text-left px-2 py-1">Window</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-right px-2 py-1">Trades</th>
                    <th className="text-right px-2 py-1">PnL</th>
                    <th className="text-right px-2 py-1">Win %</th>
                    <th className="text-right px-2 py-1">PF</th>
                    <th className="text-right px-2 py-1">Max DD%</th>
                  </tr>
                </thead>
                <tbody>
                  {compositeDoc.child_results.map((c) => (
                    <tr key={c.index} className="border-t border-border">
                      <td className="px-2 py-1 font-mono text-[10px] text-[#A0A0AB]">
                        {c.window?.start_iso?.slice(0, 10)} → {c.window?.end_iso?.slice(0, 10)}
                      </td>
                      <td className="px-2 py-1">{c.status}</td>
                      <td className="px-2 py-1 text-right font-mono">{c.summary?.total_trades ?? "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtUsd(c.summary?.total_pnl_usdt)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtPct(c.summary?.win_rate)}</td>
                      <td className="px-2 py-1 text-right font-mono">{(c.summary?.profit_factor ?? 0).toFixed(2)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtPct(c.summary?.max_drawdown_percent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Active progress */}
      {running ? (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#A0A0AB]">Running backtest <span className="font-mono text-white">{running.id.slice(0, 8)}…</span></span>
            <span className="font-mono">{(doc?.progress || 0).toFixed(1)}%</span>
          </div>
          <Progress value={doc?.progress || 0} className="h-1.5" />
          <div className="text-xs text-[#71717A]">Status: {doc?.status || "pending"} · candles fetched: {doc?.candles_fetched || 0}</div>
        </div>
      ) : null}

      {/* Results */}
      {doc && doc.status === "completed" ? (
        <ResultsErrorBoundary runId={doc.id}>
          <Results doc={doc} onRerun={() => rerunFromDoc(doc)} onDuplicate={() => duplicateFromDoc(doc)} />
        </ResultsErrorBoundary>
      ) : null}
      {doc && doc.status === "failed" ? (
        <div
          data-testid="bt-failed-banner"
          className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] rounded-sm p-4 text-sm space-y-2"
        >
          <div className="font-semibold uppercase tracking-widest text-[10px]">
            Backtest failed
          </div>
          <div className="font-mono text-xs break-all" data-testid="bt-failed-message">
            {doc.error && String(doc.error).trim()
              ? doc.error
              : "(backend reported failure with no error message — see traceback below)"}
          </div>
          {(doc.failed_phase || doc.id) ? (
            <div className="font-mono text-[10px] text-[#FF7A6E]/80">
              {doc.failed_phase ? <>phase: <span className="text-[#FF3B30]">{doc.failed_phase}</span> · </> : null}
              {doc.id ? <>run_id: <span className="text-[#A0A0AB]">{doc.id}</span></> : null}
            </div>
          ) : null}
          {doc.failed_profile_meta ? (
            <div className="font-mono text-[10px] text-[#A0A0AB]">
              profile: {doc.failed_profile_meta.profile_name || "—"}
              {" "}({doc.failed_profile_meta.profile_id || "—"}) ·
              {" "}tf: {doc.failed_profile_meta.profile_timeframe || "—"} ·
              {" "}groups: {doc.failed_profile_meta.n_condition_groups ?? "—"} ·
              {" "}flat conds: {doc.failed_profile_meta.n_flat_conditions ?? "—"}
            </div>
          ) : null}
          {doc.failed_traceback_tail ? (
            <details className="font-mono text-[10px] text-[#A0A0AB]">
              <summary className="cursor-pointer hover:text-white">traceback (last frames)</summary>
              <pre className="whitespace-pre-wrap mt-2 p-2 bg-black/30 rounded-sm">{doc.failed_traceback_tail}</pre>
            </details>
          ) : null}
        </div>
      ) : null}

      {/* Cleanup by Profit Factor — DESTRUCTIVE but dialog-gated. */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4 flex flex-wrap items-end gap-3" data-testid="bt-cleanup-panel">
        <div className="space-y-1.5 flex-1 min-w-[200px]">
          <Label className="text-xs flex items-center gap-1.5 text-[#A0A0AB]">
            <AlertTriangle size={12} strokeWidth={1.8} className="text-[#E6B800]" />
            Cleanup: delete backtests with Profit Factor below
          </Label>
          <Input
            data-testid="bt-cleanup-pf"
            type="number"
            step="0.1"
            min="0.1"
            max="10000"
            value={cleanupPf}
            onChange={(e) => setCleanupPf(e.target.value)}
            placeholder="1.2"
            className="bg-[#1A1A22] border-border rounded-sm font-mono max-w-[160px]"
          />
          <div className="text-[11px] text-[#71717A]">
            Only backtests with a finite profit_factor strictly less than this value are removed. Missing / null PFs are always kept.
          </div>
        </div>
        <Button
          data-testid="bt-cleanup-btn"
          variant="outline"
          onClick={requestCleanup}
          disabled={cleanupBusy}
          className="rounded-sm border-[#FF3B30]/50 bg-[#1A1A22] hover:bg-[#FF3B30]/15 text-[#FF3B30] disabled:opacity-40"
        >
          <Trash2 size={14} strokeWidth={1.5} className="mr-1.5" /> Delete below PF
        </Button>
      </div>

      <AlertDialog open={cleanupPreview !== null} onOpenChange={(open) => !open && setCleanupPreview(null)}>
        <AlertDialogContent data-testid="bt-cleanup-dialog" className="bg-[#0F0F14] border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#FF3B30]">
              <AlertTriangle size={18} strokeWidth={2} />
              Delete backtests below PF {cleanupPreview?.threshold}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-[#A0A0AB]">
              <span className="block">
                This will permanently remove{" "}
                <span className="text-[#FF3B30] font-mono" data-testid="bt-cleanup-count">
                  {cleanupPreview?.matching_count ?? 0}
                </span>{" "}
                backtest{(cleanupPreview?.matching_count ?? 0) === 1 ? "" : "s"} out of{" "}
                <span className="font-mono">{cleanupPreview?.total_count ?? 0}</span> total.
              </span>
              <span className="block text-[11px] text-[#71717A]">
                Only the <span className="font-mono">backtests</span> collection is affected. Strategy profiles, live signals, positions, trade history, and audit logs are not touched. Backtests without a finite profit_factor are preserved.
              </span>
              <span className="block text-[#FF3B30] text-xs">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bt-cleanup-cancel" disabled={cleanupBusy} className="rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="bt-cleanup-confirm"
              onClick={confirmCleanup}
              disabled={cleanupBusy || (cleanupPreview?.matching_count ?? 0) === 0}
              className="bg-[#FF3B30] text-white hover:bg-[#E5352B] rounded-sm disabled:opacity-40"
            >
              Delete {cleanupPreview?.matching_count ?? 0}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History */}
      <div className="bg-[#0F0F14] border border-border rounded-sm">
        <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-[#A0A0AB] flex items-center justify-between gap-3 flex-wrap">
          <span>Recent Backtests</span>
          <span className="text-[10px] normal-case tracking-normal text-[#71717A] font-mono" data-testid="bt-history-count">
            showing {visibleList.length} / {list.length}
          </span>
        </div>

        {/* Filter + bulk-delete control bar — frontend-only filters; bulk-delete
            is destructive and dialog-gated. */}
        {availableTfs.length > 0 ? (
          <div className="px-4 py-3 border-b border-border flex flex-wrap items-start gap-x-6 gap-y-3" data-testid="bt-history-controls">
            {/* Exclude-TF filter — non-destructive UI hide only */}
            <div className="flex flex-col gap-1.5 min-w-[200px]">
              <Label className="text-[10px] uppercase tracking-widest text-[#71717A] flex items-center gap-1.5">
                <EyeOff size={11} strokeWidth={1.8} /> Exclude TF (hide only)
              </Label>
              <div className="flex flex-wrap gap-1.5" data-testid="bt-exclude-tfs">
                {availableTfs.map((tf) => {
                  const checked = excludeTfs.includes(tf);
                  return (
                    <button
                      key={tf}
                      type="button"
                      data-testid={`bt-exclude-tf-${tf}`}
                      onClick={() => setExcludeTfs((prev) => checked ? prev.filter((x) => x !== tf) : [...prev, tf])}
                      className={`px-2 py-1 text-xs font-mono rounded-sm border transition-colors ${
                        checked
                          ? "bg-[#1A1A22] border-[#71717A]/50 text-[#71717A] line-through"
                          : "bg-[#1A1A22] border-border text-[#E4E4E7] hover:border-[#A0A0AB]"
                      }`}
                    >
                      {tf}
                    </button>
                  );
                })}
                {excludeTfs.length > 0 ? (
                  <button
                    type="button"
                    data-testid="bt-exclude-clear"
                    onClick={() => setExcludeTfs([])}
                    className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#A0A0AB] hover:text-white"
                  >
                    clear
                  </button>
                ) : null}
              </div>
            </div>

            {/* Bulk-delete by TF — destructive, dialog-gated */}
            <div className="flex flex-col gap-1.5 min-w-[260px]">
              <Label className="text-[10px] uppercase tracking-widest text-[#71717A] flex items-center gap-1.5">
                <Trash2 size={11} strokeWidth={1.8} className="text-[#FF3B30]" /> Bulk delete by TF
              </Label>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="bt-bulk-delete-tfs">
                {availableTfs.map((tf) => {
                  const checked = bulkDeleteTfs.includes(tf);
                  return (
                    <button
                      key={tf}
                      type="button"
                      data-testid={`bt-bulk-delete-tf-${tf}`}
                      onClick={() => setBulkDeleteTfs((prev) => checked ? prev.filter((x) => x !== tf) : [...prev, tf])}
                      className={`px-2 py-1 text-xs font-mono rounded-sm border transition-colors ${
                        checked
                          ? "bg-[#FF3B30]/15 border-[#FF3B30]/50 text-[#FF3B30]"
                          : "bg-[#1A1A22] border-border text-[#E4E4E7] hover:border-[#FF3B30]/40"
                      }`}
                    >
                      {tf}
                    </button>
                  );
                })}
                <Button
                  data-testid="bt-bulk-delete-btn"
                  variant="outline"
                  size="sm"
                  onClick={previewBulkDelete}
                  disabled={bulkBusy || bulkDeleteTfs.length === 0}
                  className="ml-1 rounded-sm border-[#FF3B30]/50 bg-[#1A1A22] hover:bg-[#FF3B30]/15 text-[#FF3B30] disabled:opacity-40"
                >
                  <Trash2 size={12} strokeWidth={1.5} className="mr-1.5" /> Delete by TF
                </Button>
              </div>
            </div>

            {/* Free-text search across config_tags + strategy name + ids */}
            <div className="flex flex-col gap-1.5 min-w-[260px] flex-1">
              <Label className="text-[10px] uppercase tracking-widest text-[#71717A] flex items-center gap-1.5">
                <Search size={11} strokeWidth={1.8} /> Search tags / strategy
              </Label>
              <div className="relative">
                <input
                  data-testid="bt-history-search"
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder='Try: "Lev=10x", "Align=20", "CVD GO", "skip"'
                  className="w-full bg-[#1A1A22] border border-border rounded-sm px-2.5 py-1.5 text-xs font-mono placeholder:text-[#52525B] focus:outline-none focus:border-[#A0A0AB]"
                />
                {searchText ? (
                  <button
                    type="button"
                    data-testid="bt-history-search-clear"
                    onClick={() => setSearchText("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-[#71717A] hover:text-white px-1.5"
                    title="Clear search"
                  >clear</button>
                ) : null}
              </div>
            </div>

            {/* Research-regime filter. Hides rows that don't match the
                chosen regime type (sourced from
                `row.config.research_regime_type`, set by the new
                Research Window preset metadata). Rows missing the
                field only show under "All". */}
            <div className="flex flex-col gap-1.5 min-w-[180px]">
              <Label className="text-[10px] uppercase tracking-widest text-[#71717A]">
                Research Regime
              </Label>
              <Select
                value={regimeFilter}
                onValueChange={(v) => setRegimeFilter(v)}
              >
                <SelectTrigger
                  className="bg-[#1A1A22] border-border rounded-sm h-7 text-xs"
                  data-testid="bt-history-regime-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="bt-history-regime-filter-all">All</SelectItem>
                  <SelectItem value="uptrend"          data-testid="bt-history-regime-filter-uptrend">Uptrend</SelectItem>
                  <SelectItem value="strong_uptrend"   data-testid="bt-history-regime-filter-strong_uptrend">Strong Uptrend</SelectItem>
                  <SelectItem value="chop"             data-testid="bt-history-regime-filter-chop">Chop</SelectItem>
                  <SelectItem value="consolidation"    data-testid="bt-history-regime-filter-consolidation">Consolidation</SelectItem>
                  <SelectItem value="downtrend"        data-testid="bt-history-regime-filter-downtrend">Downtrend</SelectItem>
                  <SelectItem value="strong_downtrend" data-testid="bt-history-regime-filter-strong_downtrend">Strong Downtrend</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bt-list">
            <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
              <tr>
                <SortableTh testid="bt-sort-created_at" label="Created" col="created_at" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <th className="text-left px-4 py-2">Strategy</th>
                <th className="text-left px-4 py-2 min-w-[260px]">Config tags</th>
                <SortableTh testid="bt-sort-tf" label="TF" col="tf" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <th className="text-left px-4 py-2">Range</th>
                <SortableTh testid="bt-sort-trades" label="Trades" col="trades" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortableTh testid="bt-sort-pnl" label="PnL" col="pnl" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortableTh testid="bt-sort-win_rate" label="Win %" col="win_rate" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortableTh testid="bt-sort-max_dd" label="Max DD" col="max_dd" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-6 text-center text-[#71717A]">No backtests yet.</td></tr>
              ) : visibleList.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-6 text-center text-[#71717A]" data-testid="bt-list-empty-filtered">
                  All {list.length} backtests are hidden by the active filters{searchText ? ` (search: "${searchText}")` : ""}.
                </td></tr>
              ) : visibleList.map((b) => (
                <tr key={b.id} className="border-t border-border/60">
                  <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{fmtTime(b.created_at)}</td>
                  <td className="px-4 py-2 text-xs text-[#E4E4E7]">{b.strategy_config_snapshot?.name || b.config?.strategy_id?.slice(0, 8) || "—"}</td>
                  <td className="px-4 py-2"><TagChips tags={tagsOf(b)} compact /></td>
                  <td className="px-4 py-2 font-mono">{b.resolved_timeframe || b.strategy_config_snapshot?.timeframe || b.config?.timeframe || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]" data-testid={`bt-row-range-${b.id}`}>
                    <div>{b.config?.start_iso?.slice(0, 16)} → {b.config?.end_iso?.slice(0, 16)}</div>
                    {b.config?.research_regime_type ? (
                      <div
                        data-testid={`bt-row-regime-tag-${b.id}`}
                        className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-sans uppercase tracking-widest text-[#F59E0B]"
                        title={b.config?.research_preset_label || b.config?.research_regime_type}
                      >
                        <span className="px-1 py-0.5 border border-[#F59E0B]/40 rounded-sm">
                          {(b.config.research_regime_type || "").replace(/_/g, " ")}
                        </span>
                        {b.config?.research_preset_label ? (
                          <span className="text-[#71717A] normal-case">
                            {b.config.research_preset_label}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 cell-num">{b.metrics?.total_trades ?? "—"}</td>
                  <td className={`px-4 py-2 cell-num ${(b.metrics?.total_pnl_usdt || 0) >= 0 ? "long-text" : "short-text"}`}>{b.metrics ? `${b.metrics.total_pnl_usdt >= 0 ? "+" : ""}$${fmtUsd(b.metrics.total_pnl_usdt)}` : "—"}</td>
                  <td className="px-4 py-2 cell-num">{b.metrics ? fmtPct(b.metrics.win_rate, 1) : "—"}</td>
                  <td className="px-4 py-2 cell-num short-text">{b.metrics ? fmtPct(-(b.metrics.max_drawdown_percent || 0), 2) : "—"}</td>
                  <td className="px-4 py-2"><Badge kind={b.status === "completed" ? "on" : b.status === "failed" ? "warn" : b.status === "cancelled" ? "off" : "neutral"}>{b.status}</Badge></td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => open(b.id)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] mr-2">Open</Button>
                    <Button size="sm" variant="outline" onClick={() => remove(b.id)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]"><Trash2 size={14} strokeWidth={1.5} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk-delete-by-TF confirmation dialog. Two-step: preview is fetched
          first; this dialog only opens once we have a non-zero matching count. */}
      <AlertDialog open={bulkDeletePreview !== null} onOpenChange={(o) => !o && setBulkDeletePreview(null)}>
        <AlertDialogContent data-testid="bt-bulk-delete-dialog" className="bg-[#0F0F14] border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#FF3B30]">
              <AlertTriangle size={18} strokeWidth={2} />
              Delete backtests by timeframe?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-[#A0A0AB]">
              <span className="block">
                This will permanently remove{" "}
                <span className="text-[#FF3B30] font-mono" data-testid="bt-bulk-delete-count">
                  {bulkDeletePreview?.matching_count ?? 0}
                </span>{" "}
                backtest{(bulkDeletePreview?.matching_count ?? 0) === 1 ? "" : "s"} matching the selected timeframe(s), out of{" "}
                <span className="font-mono">{bulkDeletePreview?.total_count ?? 0}</span> total backtest(s) on the server.
              </span>
              <span className="block text-[11px] text-[#71717A]">
                Timeframe(s):{" "}
                <span className="font-mono text-[#E4E4E7]" data-testid="bt-bulk-delete-tf-list">
                  {(bulkDeletePreview?.filters?.timeframes || bulkDeleteTfs).join(", ")}
                </span>
              </span>
              <span className="block text-[11px] text-[#71717A]">
                The Recent Backtests table above only shows the most recent {list.length} entries — older matching backtests will also be removed.
              </span>
              <span className="block text-[11px] text-[#71717A]">
                Running / pending backtests are never deleted. Strategy profiles, live signals, positions and audit logs are not touched.
              </span>
              <span className="block text-[#FF3B30] text-xs">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bt-bulk-delete-cancel" disabled={bulkBusy} className="rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="bt-bulk-delete-confirm"
              onClick={confirmBulkDelete}
              disabled={bulkBusy || (bulkDeletePreview?.matching_count ?? 0) === 0}
              className="bg-[#FF3B30] text-white hover:bg-[#E5352B] rounded-sm disabled:opacity-40"
            >
              Delete {bulkDeletePreview?.matching_count ?? 0}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Sortable column header. Click cycles asc → desc on the same column;
// switching columns resets to asc. Visual chevron tells the user which
// column drives the current sort.
function SortableTh({ testid, label, col, align, sortKey, sortDir, onClick }) {
  const active = sortKey === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-4 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onClick(col)}
        className={`inline-flex items-center gap-1 select-none uppercase tracking-widest text-[10px] transition-colors ${
          active ? "text-[#E4E4E7]" : "text-[#71717A] hover:text-[#A0A0AB]"
        } ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        <span>{label}</span>
        <Icon size={11} strokeWidth={1.8} className={active ? "opacity-100" : "opacity-50"} />
      </button>
    </th>
  );
}

// Metrics included in the delta panel (Compare with previous run).
const COMPARE_METRICS = [
  { k: "total_pnl_usdt", label: "PnL (USDT)", isPos: true,
    fmt: (v) => `${v >= 0 ? "+" : ""}$${fmtUsd(v)}`,
    fmtDelta: (d) => `${d >= 0 ? "+" : ""}$${fmtUsd(d)}` },
  { k: "win_rate", label: "Win Rate", isPos: true,
    fmt: (v) => fmtPct(v, 1),
    fmtDelta: (d) => `${d >= 0 ? "+" : ""}${d.toFixed(1)}%` },
  { k: "profit_factor", label: "Profit Factor", isPos: true,
    fmt: (v) => Number(v || 0).toFixed(2),
    fmtDelta: (d) => `${d >= 0 ? "+" : ""}${d.toFixed(2)}` },
  { k: "max_drawdown_percent", label: "Max Drawdown", isNeg: true,
    fmt: (v) => `-${fmtPct(v || 0, 2).replace("+", "")}`,
    fmtDelta: (d) => `${d >= 0 ? "+" : ""}${d.toFixed(2)}%` },
  { k: "total_trades", label: "Total Trades",
    fmt: (v) => `${v ?? 0}`,
    fmtDelta: (d) => `${d >= 0 ? "+" : ""}${d}` },
];
const COMPARE_DIAG_METRICS = [
  { k: "signals_fired", label: "Signals Fired", isPos: true },
  { k: "rejected_by_conditions", label: "Rejected by Conditions", isNeg: true },
];

function deltaTone(meta, diff) {
  if (Math.abs(diff) < 1e-9) return "text-[#A0A0AB]";
  if (meta.isPos) return diff > 0 ? "long-text" : "short-text";
  if (meta.isNeg) return diff > 0 ? "short-text" : "long-text"; // smaller is better
  return "text-[#A0A0AB]";
}

function ComparePanel({ current, previous }) {
  if (!previous) {
    return (
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4 text-sm text-[#71717A]" data-testid="bt-compare-empty">
        No previous completed run found for this strategy.
      </div>
    );
  }
  const cm = current.metrics || {};
  const pm = previous.metrics || {};
  const cd = current.diagnostics || {};
  const pd = previous.diagnostics || {};
  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm p-4 space-y-3" data-testid="bt-compare-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB]">
          Δ vs previous run
        </div>
        <div className="text-[11px] text-[#71717A] font-mono">
          current <span className="text-[#A0A0AB]">{current.id.slice(0, 8)}</span> {fmtTime(current.created_at)}
          {" "}<span className="mx-1">←</span>{" "}
          previous <span className="text-[#A0A0AB]">{previous.id.slice(0, 8)}</span> {fmtTime(previous.created_at)}
        </div>
      </div>
      <table className="w-full text-sm" data-testid="bt-compare-table">
        <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
          <tr>
            <th className="text-left px-3 py-2">Metric</th>
            <th className="text-right px-3 py-2">Previous</th>
            <th className="text-right px-3 py-2">Current</th>
            <th className="text-right px-3 py-2">Δ</th>
          </tr>
        </thead>
        <tbody>
          {COMPARE_METRICS.map((meta) => {
            const cv = Number(cm[meta.k] ?? 0);
            const pv = Number(pm[meta.k] ?? 0);
            const diff = cv - pv;
            return (
              <tr key={meta.k} className="border-t border-border/60">
                <td className="px-3 py-2 text-[#A0A0AB]">{meta.label}</td>
                <td className="px-3 py-2 cell-num">{meta.fmt(pv)}</td>
                <td className="px-3 py-2 cell-num">{meta.fmt(cv)}</td>
                <td className={`px-3 py-2 cell-num ${deltaTone(meta, diff)}`} data-testid={`bt-compare-delta-${meta.k}`}>
                  {meta.fmtDelta(diff)}
                </td>
              </tr>
            );
          })}
          {COMPARE_DIAG_METRICS.map((meta) => {
            const cv = Number(cd[meta.k] ?? 0);
            const pv = Number(pd[meta.k] ?? 0);
            const diff = cv - pv;
            return (
              <tr key={meta.k} className="border-t border-border/60">
                <td className="px-3 py-2 text-[#A0A0AB]">{meta.label}</td>
                <td className="px-3 py-2 cell-num">{pv}</td>
                <td className="px-3 py-2 cell-num">{cv}</td>
                <td className={`px-3 py-2 cell-num ${deltaTone(meta, diff)}`} data-testid={`bt-compare-delta-${meta.k}`}>
                  {`${diff >= 0 ? "+" : ""}${diff}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------
// ResultsErrorBoundary
// ---------------------------------------------------------------------
// Last-ditch safety net for the backtest result tree. Anything thrown
// during render of `<Results />` (a stale prop, a malformed metric,
// an unexpected null in a chart row, a removed field after a schema
// migration) used to unmount the whole tab and show a black screen
// — there was no fallback. Now we catch render errors here, surface
// a clear error card to the operator with the message + the run id
// (so support can pull logs), and offer "Reload page" as a recovery.
// Strategy / accounting logic is NOT touched by this — the boundary
// only affects how a render failure is presented.
class ResultsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Console-log so operators / E2E tests can see the stack.
    // eslint-disable-next-line no-console
    console.error("[Backtest result render error]", error, info);
  }
  render() {
    if (this.state.error) {
      const runId = this.props.runId || "unknown";
      const msg = String(this.state.error?.message || this.state.error || "Unknown render error");
      return (
        <div data-testid="bt-result-error-card" className="bg-[#1A1A22] border border-[#EF4444]/40 rounded-sm p-4 space-y-2">
          <div className="text-sm text-[#EF4444] font-mono">Backtest failed to load</div>
          <div className="text-xs text-[#A0A0AB]">
            The backtest ran successfully on the server, but the result UI hit
            an error while rendering. Your data is safe — this is purely a
            display issue.
          </div>
          <div className="text-[11px] font-mono text-[#71717A]">
            <div>Reason: <span className="text-[#FCD34D]">{msg}</span></div>
            <div>Run id: <span className="text-[#A0A0AB]">{runId}</span></div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              data-testid="bt-result-error-reload"
              onClick={() => window.location.reload()}
              className="text-xs px-2 py-1 border border-border rounded-sm bg-[#0F0F14] hover:bg-[#262633]"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Results({ doc, onRerun, onDuplicate }) {
  const m = doc.metrics || {};
  const trades = doc.trades || [];
  // Sanitize equity_curve before handing it to Recharts:
  //   * drop entries with missing / invalid timestamps (would throw inside
  //     `new Date(...).toISOString()` and unmount the whole Results block).
  //   * drop entries with non-finite equity (NaN / Infinity) — Recharts then
  //     fails to compute dataMin/dataMax and renders nothing.
  // Wrapping in a try/catch keeps a single bad row from blacking out the
  // entire result page (we still render an empty-state placeholder).
  const equityData = useMemo(() => {
    const raw = Array.isArray(doc.equity_curve) ? doc.equity_curve : [];
    const out = [];
    for (const p of raw) {
      if (!p || p.t == null) continue;
      const eq = Number(p.equity);
      if (!Number.isFinite(eq)) continue;
      let label;
      try {
        const d = new Date(p.t);
        const iso = d.toISOString();
        label = iso.slice(5, 16).replace("T", " ");
      } catch {
        continue;  // unparseable timestamp → skip row, keep page alive
      }
      out.push({ t: label, equity: eq });
    }
    return out;
  }, [doc.equity_curve]);
  // Compact Y-axis label formatter — prevents Recharts auto-ticks like
  // `9997.780354314022` from rendering as broken trailing-decimal noise on
  // tight equity ranges (very common in `opposite_signal` mode where each
  // trade's PnL is small relative to the balance).
  const fmtEquityTick = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}k`;
    return `$${n.toFixed(2)}`;
  };

  const [showCompare, setShowCompare] = useState(false);
  const [previousRun, setPreviousRun] = useState(null);
  const [comparing, setComparing] = useState(false);

  const fetchPrevious = async () => {
    setComparing(true);
    try {
      const list = (await api.backtestList()) || [];
      const sid = doc.strategy_config_snapshot?.id || doc.config?.strategy_id;
      if (!sid) {
        toast.error("Current run has no strategy id — cannot find a previous run.");
        return;
      }
      // List is sorted by created_at desc. Pick the first completed run for
      // the same strategy that is NOT the current doc.
      const prevSummary = list.find(
        (b) => b.status === "completed"
          && b.id !== doc.id
          && (b.strategy_config_snapshot?.id === sid || b.config?.strategy_id === sid)
      );
      if (!prevSummary) {
        setPreviousRun(null);
        setShowCompare(true);
        return;
      }
      // List omits trades + equity_curve — but metrics+diagnostics ARE included.
      // For the delta panel we only need metrics and diagnostics, so the summary
      // doc is enough. Refresh full only if needed later.
      setPreviousRun(prevSummary);
      setShowCompare(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="space-y-4">
      {doc.strategy_config_snapshot ? (
        <StrategySummary profile={doc.strategy_config_snapshot} />
      ) : null}

      <ConfigViewer doc={doc} onRerun={onRerun} onDuplicate={onDuplicate} />

      {/* Toolbar: Re-run + Compare */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          data-testid="bt-rerun"
          onClick={onRerun}
          variant="outline"
          size="sm"
          className="rounded-sm border-[#22C55E]/40 bg-[#1A1A22] hover:bg-[#22C55E]/10 text-[#22C55E] text-xs"
          title="Pre-fill the form with this run's exact config + strategy snapshot. Won't launch immediately — you can review/edit first."
        >
          <RefreshCw size={13} strokeWidth={2} className="mr-1.5" /> Re-run (pre-fill)
        </Button>
        <Button
          data-testid="bt-duplicate"
          onClick={onDuplicate}
          variant="outline"
          size="sm"
          className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
          title="Duplicate this configuration into the form for tweaking before running again."
        >
          <Copy size={13} strokeWidth={2} className="mr-1.5" /> Duplicate config
        </Button>
        <Button
          data-testid="bt-compare-prev"
          onClick={fetchPrevious}
          disabled={comparing}
          variant="outline"
          size="sm"
          className="rounded-sm border-[#3B82F6]/40 bg-[#1A1A22] hover:bg-[#3B82F6]/10 text-[#3B82F6] text-xs"
          title="Compare with the previous run for this same strategy"
        >
          <GitCompareArrows size={13} strokeWidth={2} className="mr-1.5" />
          {comparing ? "Loading…" : "Compare with previous run"}
        </Button>
        {showCompare ? (
          <Button
            onClick={() => setShowCompare(false)}
            variant="outline"
            size="sm"
            className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
          >
            Hide comparison
          </Button>
        ) : null}
      </div>

      {showCompare ? <ComparePanel current={doc} previous={previousRun} /> : null}

      {(doc.warnings || []).length > 0 ? (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] rounded-sm p-4 text-sm flex gap-3">
          <AlertTriangle size={18} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold uppercase tracking-wider text-xs mb-1">Warnings</div>
            <ul className="list-disc ml-4 space-y-0.5">
              {doc.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      ) : null}

      <DiagnosticsRow diagnostics={doc.diagnostics} />

      {m.quick_mode ? (
        <div
          data-testid="bt-quick-mode-banner"
          className="border border-[#F59E0B]/40 bg-[#F59E0B]/8 rounded-sm px-3 py-2 flex items-center gap-2 text-[11px] text-[#FFB020] font-mono"
        >
          <span className="uppercase tracking-widest">Quick Mode</span>
          <span className="text-muted-foreground">·</span>
          <span>
            Advanced analytics skipped (behavior + duration analytics +
            per-trade MFE/MAE). Core metrics — PnL, trades, win rate,
            profit factor, drawdown, equity — are identical to a full
            run. Re-run with Quick Mode OFF for deep analysis.
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testid="bt-total-pnl" label="Total PnL" value={`${(m.total_pnl_usdt || 0) >= 0 ? "+" : ""}$${fmtUsd(m.total_pnl_usdt || 0)}`} sublabel={fmtPct(m.total_pnl_percent || 0)} accent={(m.total_pnl_usdt || 0) >= 0 ? "long-text" : "short-text"} />
        <StatCard testid="bt-trades" label="Trades" value={m.total_trades ?? 0} sublabel={`${m.long_trades || 0}L / ${m.short_trades || 0}S`} />
        <StatCard testid="bt-winrate" label="Win Rate" value={fmtPct(m.win_rate || 0, 1)} sublabel={`${m.wins || 0}W / ${m.losses || 0}L`} />
        <StatCard testid="bt-pf" label="Profit Factor" value={Number(m.profit_factor || 0).toFixed(2)} />
        <StatCard testid="bt-maxdd" label="Max Drawdown" value={`-${fmtPct(m.max_drawdown_percent || 0, 2).replace("+", "")}`} sublabel={`-$${fmtUsd(m.max_drawdown_usdt || 0)}`} accent="short-text" />
        <StatCard testid="bt-fees" label="Total Fees" value={`$${fmtUsd(m.total_fees_usdt || 0)}`} />

        <StatCard label="Avg Win" value={`$${fmtUsd(m.avg_win_usdt || 0)}`} accent="long-text" />
        <StatCard label="Avg Loss" value={`$${fmtUsd(m.avg_loss_usdt || 0)}`} accent="short-text" />
        <StatCard label="Best Trade" value={`$${fmtUsd(m.best_trade_usdt || 0)}`} accent="long-text" />
        <StatCard label="Worst Trade" value={`$${fmtUsd(m.worst_trade_usdt || 0)}`} accent="short-text" />
        <StatCard label="Avg Duration" value={`${(m.avg_trade_duration_minutes || 0).toFixed(1)}m`} />
        <StatCard label="Final Equity" value={`$${fmtUsd(m.final_equity || 0)}`} />
      </div>

      <DurationAnalyticsCard analytics={m.duration_analytics} />
      <BehaviorAnalyticsCard analytics={m.behavior_analytics} />

      {/* Per-regime metrics — only renders when regime gate produced
          tagged trades. Minimal table, no styling polish. */}
      {doc?.per_regime_metrics && Object.keys(doc.per_regime_metrics).length > 0 ? (
        <div
          data-testid="bt-per-regime-card"
          className="bg-[#0F0F14] border border-border rounded-sm p-4"
        >
          <div className="text-xs uppercase tracking-widest text-[#A0A0AB] mb-3">
            Per-Regime Metrics
            <span className="ml-2 text-[10px] text-[#10B981] font-mono normal-case">
              (regime gate active)
            </span>
          </div>
          <table className="w-full text-xs font-mono">
            <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
              <tr>
                <th className="text-left py-1">Regime</th>
                <th className="text-right py-1">Trades</th>
                <th className="text-right py-1">Wins</th>
                <th className="text-right py-1">Losses</th>
                <th className="text-right py-1">Win Rate</th>
                <th className="text-right py-1">Avg %</th>
                <th className="text-right py-1">Realized PnL</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(doc.per_regime_metrics)
                .sort((a, b) => (b[1].count || 0) - (a[1].count || 0))
                .map(([label, cell]) => (
                  <tr key={label} className="border-t border-border/30">
                    <td className="py-1 text-[#E4E4E7]">{label}</td>
                    <td className="py-1 text-right">{cell.count || 0}</td>
                    <td className="py-1 text-right text-[#10B981]">{cell.wins || 0}</td>
                    <td className="py-1 text-right text-[#FF3B30]">{cell.losses || 0}</td>
                    <td className="py-1 text-right">{fmtPct((cell.win_rate || 0) * 100, 1)}</td>
                    <td className="py-1 text-right">{fmtPct(cell.avg_pnl_percent || 0, 2)}</td>
                    <td className={`py-1 text-right ${(cell.realized_pnl || 0) >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
                      {(cell.realized_pnl || 0) >= 0 ? "+" : ""}${fmtUsd(cell.realized_pnl || 0)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="bg-[#0F0F14] border border-border rounded-sm p-4">
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB] mb-3">Equity Curve</div>
        <div className="h-72" data-testid="bt-equity-chart">
          {equityData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-[#71717A]" data-testid="bt-equity-empty">
              No equity data to display
              {trades.length === 0 ? " — backtest produced zero trades" : ""}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <RLineChart data={equityData} margin={{ top: 10, right: 12, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="t" tick={{ fill: "#71717A", fontSize: 10 }} stroke="#27272A" minTickGap={40} />
                <YAxis
                  tick={{ fill: "#71717A", fontSize: 10 }}
                  stroke="#27272A"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={fmtEquityTick}
                  width={68}
                />
                <RTooltip
                  contentStyle={{ background: "#0F0F14", border: "1px solid #27272A", borderRadius: 2, fontSize: 12 }}
                  labelStyle={{ color: "#A0A0AB" }}
                  formatter={(v) => [fmtEquityTick(v), "Equity"]}
                />
                <Line type="monotone" dataKey="equity" stroke="#00E676" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </RLineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <TradeInspectionPanel
        trades={trades}
        metrics={m}
        backtestId={doc.id}
      />
    </div>
  );
}


// ===========================================================================
// TradeInspectionPanel — interactive trade-level inspection / outlier UI.
// Replaces the static Trades table. All filtering / sorting / Top-N / row
// expansion happens CLIENT-SIDE on the trades array already loaded with
// the backtest doc. NEVER refetches. NEVER mutates the trades. Memoized
// derivations keep large backtests (thousands of trades) responsive:
//   * useMemo on the sort+filter pipeline (deps: trades, mode, sortKey,
//     sortDir, topN, exitFilter)
//   * row expansion is local state keyed by trade id; expanded rows
//     render the full detail block on demand
//   * insights computed once from the FULL trade list (not the visible
//     slice) so they are stable across preset toggles
//
// IMPORTANT: this panel is read-only. It does NOT change strategy logic,
// sizing, accounting, exits, or any backtest metric. Server payload is
// the source of truth — we just present it.
// ===========================================================================
const INSPECT_LS_PRESET = "btcbot.inspect.preset";
const INSPECT_LS_SORT = "btcbot.inspect.sort_key";
const INSPECT_LS_DIR = "btcbot.inspect.sort_dir";
const INSPECT_LS_TOPN = "btcbot.inspect.top_n";
const INSPECT_LS_EXITF = "btcbot.inspect.exit_filter";

const PRESETS = [
  { key: "default", label: "All trades", sort: { key: "opened_at_ms", dir: "asc" }, exitFilter: "all" },
  { key: "biggest_winners", label: "Biggest Winners", sort: { key: "realized_pnl", dir: "desc" }, exitFilter: "all" },
  { key: "biggest_losers", label: "Biggest Losers", sort: { key: "realized_pnl", dir: "asc" }, exitFilter: "all" },
  { key: "worst_dd", label: "Worst DD Spikes", sort: { key: "drawdown_after_percent", dir: "desc" }, exitFilter: "all" },
  { key: "highest_mae", label: "Highest MAE", sort: { key: "mae_pct", dir: "desc" }, exitFilter: "all" },
  { key: "highest_mfe", label: "Highest MFE", sort: { key: "mfe_pct", dir: "desc" }, exitFilter: "all" },
  { key: "longest", label: "Longest Trades", sort: { key: "duration_ms", dir: "desc" }, exitFilter: "all" },
  { key: "fastest", label: "Fastest Trades", sort: { key: "duration_ms", dir: "asc" }, exitFilter: "all" },
  { key: "exit_opposite", label: "Opposite Signal Exits", sort: { key: "realized_pnl", dir: "asc" }, exitFilter: "opposite_signal" },
  { key: "exit_sl", label: "Stop Loss Exits", sort: { key: "realized_pnl", dir: "asc" }, exitFilter: "stop_loss" },
  { key: "exit_tp", label: "Take Profit Exits", sort: { key: "realized_pnl", dir: "desc" }, exitFilter: "take_profit" },
];

const TOP_N_OPTIONS = [
  { key: "10", label: "Top 10", value: 10 },
  { key: "20", label: "Top 20", value: 20 },
  { key: "50", label: "Top 50", value: 50 },
  { key: "all", label: "All", value: null },
];

// Numeric accessors for every sortable column. Returns null for missing
// values so they can be pushed to the bottom on either direction.
const TRADE_ACCESSORS = {
  opened_at_ms: (t) => t?.opened_at_ms ?? null,
  closed_at_ms: (t) => t?.closed_at_ms ?? null,
  duration_ms: (t) => {
    const a = t?.opened_at_ms;
    const b = t?.closed_at_ms;
    return (a != null && b != null) ? (b - a) : null;
  },
  realized_pnl: (t) => (t?.realized_pnl ?? null),
  pnl_percent: (t) => (t?.pnl_percent ?? null),
  equity_after_usdt: (t) => (t?.equity_after_usdt ?? null),
  equity_change_usdt: (t) => (t?.equity_change_usdt ?? null),
  drawdown_after_percent: (t) => (t?.drawdown_after_percent ?? null),
  mfe_pct: (t) => (t?.mfe_pct ?? null),
  mae_pct: (t) => (t?.mae_pct ?? null),
  entry_price: (t) => (t?.entry_price ?? null),
  exit_price: (t) => (t?.exit_price ?? null),
};

function _loadInspectState() {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return { preset: "default", sortKey: "opened_at_ms", sortDir: "asc", topN: "all", exitFilter: "all" };
    }
    const ls = window.localStorage;
    return {
      preset: ls.getItem(INSPECT_LS_PRESET) || "default",
      sortKey: ls.getItem(INSPECT_LS_SORT) || "opened_at_ms",
      sortDir: ls.getItem(INSPECT_LS_DIR) || "asc",
      topN: ls.getItem(INSPECT_LS_TOPN) || "all",
      exitFilter: ls.getItem(INSPECT_LS_EXITF) || "all",
    };
  } catch {
    return { preset: "default", sortKey: "opened_at_ms", sortDir: "asc", topN: "all", exitFilter: "all" };
  }
}

function _persistInspect(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch { /* swallow */ }
}

// Format a duration in ms as "1h 23m" / "5m 12s" / "300ms".
function _fmtDur(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

// Compute insights from the FULL trade list (not the visible slice).
// Each insight is a deterministic fact derived from the data — never
// AI-generated. Returns an array of { id, label, value } objects;
// missing-data insights are silently dropped so the panel stays clean
// on backtests that don't have enough samples.
function _computeInsights(trades) {
  const out = [];
  if (!Array.isArray(trades) || trades.length === 0) return out;

  const losers = trades.filter((t) => (t.realized_pnl ?? 0) < 0);
  const winners = trades.filter((t) => (t.realized_pnl ?? 0) > 0);
  const total = trades.length;

  // 1. % of losers exited by opposite_signal
  if (losers.length > 0) {
    const oppLosers = losers.filter((t) => t.exit_reason === "opposite_signal").length;
    const pct = (oppLosers / losers.length) * 100;
    if (pct > 0) {
      out.push({
        id: "losers_by_opposite",
        label: `${pct.toFixed(0)}% of losing trades exited by opposite_signal`,
        value: `${oppLosers} of ${losers.length} losers`,
      });
    }
  }

  // 2. Average MAE on the bottom-decile losers (the worst 10%)
  if (losers.length >= 5) {
    const sorted = [...losers].sort((a, b) => (a.realized_pnl ?? 0) - (b.realized_pnl ?? 0));
    const cutoff = Math.max(1, Math.floor(sorted.length * 0.1));
    const tail = sorted.slice(0, cutoff);
    const maes = tail.map((t) => Number(t.mae_pct ?? 0)).filter((v) => Number.isFinite(v) && v > 0);
    if (maes.length > 0) {
      const avg = maes.reduce((s, v) => s + v, 0) / maes.length;
      out.push({
        id: "worst_losers_mae",
        label: `Worst ${cutoff} losers had avg MAE ${avg.toFixed(2)}%`,
        value: `n=${maes.length}`,
      });
    }
  }

  // 3. Average duration of top-decile winners
  if (winners.length >= 5) {
    const sorted = [...winners].sort((a, b) => (b.realized_pnl ?? 0) - (a.realized_pnl ?? 0));
    const cutoff = Math.max(1, Math.floor(sorted.length * 0.1));
    const head = sorted.slice(0, cutoff);
    const durs = head
      .map((t) => (t.opened_at_ms != null && t.closed_at_ms != null) ? (t.closed_at_ms - t.opened_at_ms) : null)
      .filter((d) => Number.isFinite(d) && d > 0);
    if (durs.length > 0) {
      const avg = durs.reduce((s, v) => s + v, 0) / durs.length;
      out.push({
        id: "top_winners_duration",
        label: `Top ${cutoff} winners lasted on avg ${_fmtDur(avg)}`,
        value: `n=${durs.length}`,
      });
    }
  }

  // 4. Stop-loss exit recovery — % of trades where the next trade in
  //    the timeline (any side) was profitable. Heuristic for "did the
  //    market recover after stops?".
  const slExits = trades
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => t.exit_reason === "stop_loss");
  if (slExits.length >= 5) {
    let recovered = 0;
    let counted = 0;
    for (const { i } of slExits) {
      const next = trades[i + 1];
      if (next && Number.isFinite(next.realized_pnl)) {
        counted += 1;
        if (next.realized_pnl > 0) recovered += 1;
      }
    }
    if (counted > 0) {
      const pct = (recovered / counted) * 100;
      out.push({
        id: "sl_recovery",
        label: `${pct.toFixed(0)}% of stop_loss exits were followed by a profitable next trade`,
        value: `${recovered} of ${counted}`,
      });
    }
  }

  // 5. Long vs short imbalance (raw count)
  const longs = trades.filter((t) => t.side === "LONG").length;
  const shorts = total - longs;
  if (total >= 10) {
    const dom = longs > shorts ? "LONG" : (shorts > longs ? "SHORT" : null);
    if (dom) {
      const pct = (Math.max(longs, shorts) / total) * 100;
      out.push({
        id: "side_imbalance",
        label: `${pct.toFixed(0)}% of trades were ${dom}`,
        value: `${longs}L / ${shorts}S`,
      });
    }
  }

  return out;
}

function TradeInspectionPanel({ trades, metrics, backtestId }) {
  const initial = useMemo(_loadInspectState, []);
  const [preset, setPreset] = useState(initial.preset);
  const [sortKey, setSortKey] = useState(initial.sortKey);
  const [sortDir, setSortDir] = useState(initial.sortDir);
  const [topN, setTopN] = useState(initial.topN);
  const [exitFilter, setExitFilter] = useState(initial.exitFilter);
  const [expandedId, setExpandedId] = useState(null);

  // Persist user's choices on every change. Cheap; localStorage
  // writes are <1ms each in practice.
  useEffect(() => { _persistInspect(INSPECT_LS_PRESET, preset); }, [preset]);
  useEffect(() => { _persistInspect(INSPECT_LS_SORT, sortKey); }, [sortKey]);
  useEffect(() => { _persistInspect(INSPECT_LS_DIR, sortDir); }, [sortDir]);
  useEffect(() => { _persistInspect(INSPECT_LS_TOPN, topN); }, [topN]);
  useEffect(() => { _persistInspect(INSPECT_LS_EXITF, exitFilter); }, [exitFilter]);

  const applyPreset = (p) => {
    setPreset(p.key);
    setSortKey(p.sort.key);
    setSortDir(p.sort.dir);
    setExitFilter(p.exitFilter);
    setExpandedId(null);
  };

  // The visible trades pipeline: filter → sort → top-N. Memoized on
  // every input so 1000+ trade backtests don't burn CPU on each render.
  const visible = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    let rows = trades;
    if (exitFilter && exitFilter !== "all") {
      rows = rows.filter((t) => t.exit_reason === exitFilter);
    }
    const acc = TRADE_ACCESSORS[sortKey] || TRADE_ACCESSORS.opened_at_ms;
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;   // nulls last regardless of direction
      if (vb === null) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    const topNVal = TOP_N_OPTIONS.find((o) => o.key === topN)?.value;
    if (topNVal != null) rows = rows.slice(0, topNVal);
    return rows;
  }, [trades, sortKey, sortDir, exitFilter, topN]);

  // Quantile boundaries used by row highlight colour. Computed from the
  // FULL trades array — keeping highlights consistent across preset
  // toggles. `null` when not enough samples.
  const highlightStats = useMemo(() => {
    if (!Array.isArray(trades) || trades.length < 5) return null;
    const pnls = trades.map((t) => t.realized_pnl ?? 0).filter(Number.isFinite);
    const sorted = [...pnls].sort((a, b) => a - b);
    const q = (p) => sorted[Math.max(0, Math.floor(sorted.length * p) - 1)] ?? 0;
    const maes = trades.map((t) => t.mae_pct ?? 0).filter((v) => v > 0).sort((a, b) => a - b);
    const mfes = trades.map((t) => t.mfe_pct ?? 0).filter((v) => v > 0).sort((a, b) => a - b);
    const dur = trades
      .map((t) => (t.opened_at_ms != null && t.closed_at_ms != null) ? (t.closed_at_ms - t.opened_at_ms) : null)
      .filter((d) => Number.isFinite(d) && d > 0)
      .sort((a, b) => a - b);
    return {
      lossP10: q(0.10),
      gainP90: q(0.90),
      maeP90: maes[Math.max(0, Math.floor(maes.length * 0.9) - 1)] ?? null,
      mfeP90: mfes[Math.max(0, Math.floor(mfes.length * 0.9) - 1)] ?? null,
      durP90: dur[Math.max(0, Math.floor(dur.length * 0.9) - 1)] ?? null,
      maxDdPct: metrics?.max_drawdown_percent ?? 0,
    };
  }, [trades, metrics]);

  const insights = useMemo(() => _computeInsights(trades), [trades]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(["realized_pnl", "pnl_percent", "equity_after_usdt", "equity_change_usdt", "mfe_pct"].includes(key) ? "desc" : "asc");
    }
    setPreset("default");
  };

  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm" data-testid="bt-inspect">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB]">
          Trade Inspection
          <span className="normal-case tracking-normal text-[#71717A] ml-2">
            ({visible.length} of {trades.length})
          </span>
        </div>
        <a
          data-testid="bt-export-csv"
          href={api.backtestCsvUrl(backtestId)}
          download
          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border border-border bg-[#1A1A22] hover:bg-[#262633] text-white normal-case tracking-normal"
        >
          <Download size={12} strokeWidth={1.5} /> Export CSV
        </a>
      </div>

      {/* Presets row */}
      <div className="px-4 py-2 border-b border-border flex flex-wrap gap-1.5" data-testid="bt-inspect-presets">
        {PRESETS.map((p) => {
          const active = preset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              data-testid={`bt-preset-${p.key}`}
              data-active={active ? "true" : "false"}
              onClick={() => applyPreset(p)}
              className={[
                "text-xs px-2 h-7 rounded-sm border transition-colors",
                active
                  ? "bg-[#3B82F6]/15 border-[#3B82F6] text-[#3B82F6]"
                  : "bg-[#1A1A22] border-border text-[#A0A0AB] hover:bg-[#262633]",
              ].join(" ")}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Top-N + exit filter row */}
      <div className="px-4 py-2 border-b border-border flex flex-wrap gap-3 items-center text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-[#71717A] uppercase tracking-widest text-[10px]">Show</span>
          {TOP_N_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              data-testid={`bt-topn-${o.key}`}
              data-active={topN === o.key ? "true" : "false"}
              onClick={() => setTopN(o.key)}
              className={[
                "px-2 h-6 rounded-sm border",
                topN === o.key
                  ? "bg-[#3B82F6]/15 border-[#3B82F6] text-[#3B82F6]"
                  : "bg-[#1A1A22] border-border text-[#A0A0AB] hover:bg-[#262633]",
              ].join(" ")}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#71717A] uppercase tracking-widest text-[10px]">Exit</span>
          {[
            { k: "all", l: "All" },
            { k: "take_profit", l: "TP" },
            { k: "stop_loss", l: "SL" },
            { k: "opposite_signal", l: "Opposite" },
            { k: "max_duration", l: "Time" },
            { k: "end_of_data", l: "End" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              data-testid={`bt-exit-${opt.k}`}
              data-active={exitFilter === opt.k ? "true" : "false"}
              onClick={() => { setExitFilter(opt.k); setPreset("default"); }}
              className={[
                "px-2 h-6 rounded-sm border",
                exitFilter === opt.k
                  ? "bg-[#3B82F6]/15 border-[#3B82F6] text-[#3B82F6]"
                  : "bg-[#1A1A22] border-border text-[#A0A0AB] hover:bg-[#262633]",
              ].join(" ")}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>

      {/* Insights panel — derived ONLY from data, never AI/inferred. */}
      {insights.length > 0 ? (
        <div data-testid="bt-inspect-insights" className="px-4 py-2 border-b border-border flex flex-wrap gap-2 text-[11px]">
          {insights.map((i) => (
            <div
              key={i.id}
              data-testid={`bt-insight-${i.id}`}
              className="inline-flex items-center gap-2 px-2 py-1 rounded-sm bg-[#1A1A22] border border-border"
            >
              <span className="text-[#A0A0AB]">{i.label}</span>
              <span className="font-mono text-[10px] text-[#71717A]">{i.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Trades table */}
      {(() => {
        // Phase 4 safety: cap rendered rows to keep large backtests
        // (>500 trades) from black-screening the browser. Existing
        // CSV export endpoint streams the full set unmodified, so no
        // data is hidden — only the DOM payload is bounded.
        const TRADE_TABLE_CAP = 500;
        const capped = visible.length > TRADE_TABLE_CAP;
        const rows = capped ? visible.slice(0, TRADE_TABLE_CAP) : visible;
        return (
        <div className="overflow-x-auto max-h-[32rem]">
        {capped ? (
          <div
            data-testid="bt-trades-cap-notice"
            className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#FFB020] bg-[#F59E0B]/8 border-b border-[#F59E0B]/30 sticky top-0 z-20"
          >
            Showing first {TRADE_TABLE_CAP.toLocaleString()} of{" "}
            {visible.length.toLocaleString()} rows · apply a filter or
            download CSV to inspect the rest
          </div>
        ) : null}
        <table className="w-full text-sm" data-testid="bt-trades">
          <thead className="text-[10px] uppercase tracking-widest text-[#71717A] sticky top-0 bg-[#0F0F14] z-10">
            <tr>
              <_Th label="Opened" col="opened_at_ms" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
              <_Th label="Closed" col="closed_at_ms" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
              <_Th label="Dur" col="duration_ms" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <th className="text-left px-3 py-2">Side</th>
              <_Th label="Entry" col="entry_price" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="Exit" col="exit_price" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <th className="text-right px-3 py-2">Lev</th>
              <_Th label="PnL" col="realized_pnl" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="PnL %" col="pnl_percent" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="Equity" col="equity_after_usdt" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="Δ Eq" col="equity_change_usdt" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="DD %" col="drawdown_after_percent" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="MFE %" col="mfe_pct" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <_Th label="MAE %" col="mae_pct" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              <th className="text-left px-3 py-2">Exit reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={15} className="px-4 py-6 text-center text-[#71717A]" data-testid="bt-trades-empty">
                {trades.length === 0 ? "No trades." : "No trades match this filter."}
              </td></tr>
            ) : rows.map((t, idx) => (
              <_TradeRow
                key={t.id || `${t.opened_at_ms}-${idx}`}
                trade={t}
                stats={highlightStats}
                expanded={expandedId === t.id}
                onToggle={() => setExpandedId((cur) => cur === t.id ? null : t.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
        );
      })()}
    </div>
  );
}

// Sortable header cell. Mirrors the existing `SortableTh` used by the
// recent-backtests table but is slightly tighter (px-3, h-7) so the
// 15-column trade table fits without horizontal pain.
function _Th({ label, col, sortKey, sortDir, onClick, align }) {
  const active = sortKey === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"} cursor-pointer select-none`}
      onClick={() => onClick(col)}
      data-testid={`bt-sort-${col}`}
      data-active={active ? "true" : "false"}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <Icon size={11} strokeWidth={1.5} className={active ? "text-[#3B82F6]" : "text-[#71717A]"} />
        <span>{label}</span>
      </span>
    </th>
  );
}

// Single trade row. Expansion is controlled by the parent so only ONE
// row's detail block is rendered at a time — keeps the DOM small even
// on big backtests. Highlight tones come from quantile boundaries
// computed once in the parent and forwarded as `stats`.
function _TradeRow({ trade: t, stats, expanded, onToggle }) {
  const pnl = t.realized_pnl ?? 0;
  const ddPct = t.drawdown_after_percent ?? 0;
  const dur = (t.opened_at_ms != null && t.closed_at_ms != null) ? (t.closed_at_ms - t.opened_at_ms) : null;

  // Highlight selection — mutually exclusive, applied in priority order
  // so a row never gets two competing tones. Trader-terminal aesthetic:
  //   * red    = bottom-decile loser
  //   * green  = top-decile winner
  //   * orange = high DD spike
  //   * blue   = high MFE
  //   * purple = unusually long duration
  let tone = "";
  if (stats) {
    if (pnl <= stats.lossP10 && pnl < 0) tone = "bg-[#FF3B30]/12";
    else if (pnl >= stats.gainP90 && pnl > 0) tone = "bg-[#00E676]/10";
    else if (stats.maxDdPct > 0 && ddPct >= 0.5 * stats.maxDdPct) tone = "bg-[#F59E0B]/10";
    else if (stats.mfeP90 != null && (t.mfe_pct ?? 0) >= stats.mfeP90 && (t.mfe_pct ?? 0) > 0) tone = "bg-[#3B82F6]/10";
    else if (stats.durP90 != null && dur != null && dur >= stats.durP90) tone = "bg-[#A855F7]/10";
  }

  return (
    <>
      <tr
        className={`border-t border-border/60 cursor-pointer hover:bg-[#1A1A22]/60 ${tone}`}
        data-testid={`bt-trade-row${expanded ? "-expanded" : ""}`}
        onClick={onToggle}
      >
        <td className="px-3 py-1.5 font-mono text-xs text-[#A0A0AB] whitespace-nowrap">{fmtTime(t.opened_at_iso)}</td>
        <td className="px-3 py-1.5 font-mono text-xs text-[#A0A0AB] whitespace-nowrap">{fmtTime(t.closed_at_iso)}</td>
        <td className="px-3 py-1.5 cell-num text-xs">{_fmtDur(dur)}</td>
        <td className="px-3 py-1.5"><Badge kind={t.side === "LONG" ? "long" : "short"}>{t.side}</Badge></td>
        <td className="px-3 py-1.5 cell-num">${fmtUsd(t.entry_price)}</td>
        <td className="px-3 py-1.5 cell-num">${fmtUsd(t.exit_price || 0)}</td>
        <td className="px-3 py-1.5 cell-num">{t.leverage}x</td>
        <td className={`px-3 py-1.5 cell-num ${pnl >= 0 ? "long-text" : "short-text"}`}>{pnl >= 0 ? "+" : ""}${fmtUsd(pnl)}</td>
        <td className={`px-3 py-1.5 cell-num ${(t.pnl_percent || 0) >= 0 ? "long-text" : "short-text"}`}>{fmtPct(t.pnl_percent || 0)}</td>
        <td className="px-3 py-1.5 cell-num">${fmtUsd(t.equity_after_usdt || 0)}</td>
        <td className={`px-3 py-1.5 cell-num ${(t.equity_change_usdt || 0) >= 0 ? "long-text" : "short-text"}`}>
          {(t.equity_change_usdt || 0) >= 0 ? "+" : ""}${fmtUsd(t.equity_change_usdt || 0)}
        </td>
        <td className="px-3 py-1.5 cell-num text-[#A0A0AB]">-{(ddPct || 0).toFixed(2)}%</td>
        <td className="px-3 py-1.5 cell-num text-[#A0A0AB]">{(t.mfe_pct || 0).toFixed(2)}%</td>
        <td className="px-3 py-1.5 cell-num text-[#A0A0AB]">{(t.mae_pct || 0).toFixed(2)}%</td>
        <td className="px-3 py-1.5 text-xs text-[#A0A0AB]">{t.exit_reason}</td>
      </tr>
      {expanded ? (
        <tr data-testid={`bt-trade-detail-${t.id}`}>
          <td colSpan={15} className="px-4 py-3 bg-[#0B0B10] border-t border-border/60">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs font-mono">
              <_DetailRow k="id" v={t.id} />
              <_DetailRow k="side" v={t.side} />
              <_DetailRow k="entry" v={`$${fmtUsd(t.entry_price)}`} />
              <_DetailRow k="exit" v={`$${fmtUsd(t.exit_price || 0)}`} />
              <_DetailRow k="size" v={`${Number(t.size || 0).toFixed(6)} BTC`} />
              <_DetailRow k="notional" v={`$${fmtUsd(t.notional || 0)}`} />
              <_DetailRow k="leverage" v={`${t.leverage}x`} />
              <_DetailRow k="duration" v={_fmtDur(dur)} />
              <_DetailRow k="opened_at" v={fmtTime(t.opened_at_iso)} />
              <_DetailRow k="closed_at" v={fmtTime(t.closed_at_iso)} />
              <_DetailRow k="realized_pnl" v={`${pnl >= 0 ? "+" : ""}$${fmtUsd(pnl)}`} />
              <_DetailRow k="pnl_pct" v={fmtPct(t.pnl_percent || 0)} />
              <_DetailRow k="fees_paid" v={`$${fmtUsd(t.fees_paid || 0)}`} />
              <_DetailRow k="equity_after" v={`$${fmtUsd(t.equity_after_usdt || 0)}`} />
              <_DetailRow k="dd_after" v={`${(ddPct || 0).toFixed(2)}%`} />
              <_DetailRow k="exit_reason" v={t.exit_reason || "—"} />
              <_DetailRow k="MFE %" v={`${(t.mfe_pct || 0).toFixed(2)}%`} />
              <_DetailRow k="MFE at" v={t.mfe_at_ms ? new Date(t.mfe_at_ms).toISOString().slice(5, 16).replace("T", " ") : "—"} />
              <_DetailRow k="MAE %" v={`${(t.mae_pct || 0).toFixed(2)}%`} />
              <_DetailRow k="MAE at" v={t.mae_at_ms ? new Date(t.mae_at_ms).toISOString().slice(5, 16).replace("T", " ") : "—"} />
            </div>
            {Array.isArray(t.conditions) && t.conditions.length > 0 ? (
              <div className="mt-3 pt-2 border-t border-border/40">
                <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Entry Conditions</div>
                <div className="flex flex-wrap gap-1.5">
                  {t.conditions.map((c, i) => (
                    <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[#1A1A22] border border-border text-[#A0A0AB]">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function _DetailRow({ k, v }) {
  return (
    <>
      <span className="text-[#71717A]">{k}</span>
      <span className="text-[#A0A0AB] truncate" title={String(v)}>{String(v ?? "—")}</span>
    </>
  );
}


// ---------------------------------------------------------------------------
// Trade Duration Analytics card. Read-only — derived entirely from
// `metrics.duration_analytics` (see backend/services/backtest_engine.py).
// All durations are in MINUTES. Open-at-end policy: positions still open
// at the end of the backtest are EXCLUDED from every stat below; their
// count is shown separately in the subtitle.
// ---------------------------------------------------------------------------
function _fmtMin(n) {
  if (!Number.isFinite(n) || n <= 0) return "0m";
  if (n < 60) return `${n.toFixed(1)}m`;
  const h = Math.floor(n / 60);
  const rem = n - h * 60;
  return rem < 0.05 ? `${h}h` : `${h}h ${rem.toFixed(0)}m`;
}

function _DurStat({ label, stats, testid }) {
  const c = stats?.count ?? 0;
  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono">
        <span className="text-[#71717A]">avg</span><span className="text-right">{_fmtMin(stats?.mean ?? 0)}</span>
        <span className="text-[#71717A]">med</span><span className="text-right">{_fmtMin(stats?.median ?? 0)}</span>
        <span className="text-[#71717A]">min</span><span className="text-right">{_fmtMin(stats?.min ?? 0)}</span>
        <span className="text-[#71717A]">max</span><span className="text-right">{_fmtMin(stats?.max ?? 0)}</span>
        <span className="text-[#71717A]">n</span><span className="text-right">{c}</span>
      </div>
    </div>
  );
}

function DurationAnalyticsCard({ analytics }) {
  if (!analytics || (analytics.total?.count ?? 0) === 0) {
    return (
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4" data-testid="bt-duration-empty">
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB] mb-1">Trade Duration Analytics</div>
        <div className="text-xs text-[#71717A]">No closed trades — duration metrics unavailable.</div>
      </div>
    );
  }
  const a = analytics;
  const buckets = a.bucket_order || ["<5m", "5-15m", "15-30m", "30-60m", "1-2h", "2h+"];
  const counts = a.bucket_counts || {};
  const pcts = a.bucket_distribution_pct || {};
  const maxBucket = Math.max(1, ...buckets.map((b) => counts[b] || 0));

  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm p-4 space-y-4" data-testid="bt-duration-analytics">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A0A0AB]">Trade Duration Analytics</div>
          <div className="text-[11px] text-[#71717A] mt-1">
            Closed trades only. {a.open_at_end_count > 0
              ? <>Open-at-end <span className="font-mono text-[#F59E0B]">{a.open_at_end_count}</span> trade{a.open_at_end_count === 1 ? "" : "s"} excluded from stats.</>
              : <>No trades open at end of run.</>}
          </div>
        </div>
        <div className="text-[11px] text-[#71717A]">All durations in {a.unit || "minutes"}</div>
      </div>

      {/* All / Wins / Losses */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <_DurStat label="All trades" stats={a.total} testid="bt-dur-all" />
        <_DurStat label="Winning trades" stats={a.wins} testid="bt-dur-wins" />
        <_DurStat label="Losing trades" stats={a.losses} testid="bt-dur-losses" />
      </div>

      {/* LONG / SHORT split */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <_DurStat label="LONG · all" stats={a.long} testid="bt-dur-long" />
        <_DurStat label="SHORT · all" stats={a.short} testid="bt-dur-short" />
      </div>

      {/* Side × win/loss matrix */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <_DurStat label="LONG · wins" stats={a.long_wins} testid="bt-dur-long-wins" />
        <_DurStat label="LONG · losses" stats={a.long_losses} testid="bt-dur-long-losses" />
        <_DurStat label="SHORT · wins" stats={a.short_wins} testid="bt-dur-short-wins" />
        <_DurStat label="SHORT · losses" stats={a.short_losses} testid="bt-dur-short-losses" />
      </div>

      {/* Distribution histogram */}
      <div data-testid="bt-dur-buckets">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Distribution</div>
        <div className="space-y-1.5">
          {buckets.map((b) => {
            const cnt = counts[b] || 0;
            const pct = pcts[b] || 0;
            const width = `${(cnt / maxBucket) * 100}%`;
            return (
              <div key={b} className="flex items-center gap-3 text-xs font-mono">
                <span className="w-14 text-[#A0A0AB]">{b}</span>
                <div className="flex-1 h-4 bg-[#1A1A22] rounded-sm overflow-hidden">
                  <div className="h-full bg-[#22C55E]/40 border-r border-[#22C55E]" style={{ width }} />
                </div>
                <span className="w-10 text-right text-[#A0A0AB]">{cnt}</span>
                <span className="w-14 text-right text-[#71717A]">{pct.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Trade Behavior Analytics card. Read-only — derived from
// `metrics.behavior_analytics` (services/backtest_engine.py).
// 5 subsections: Favorable Before Failure, Adverse Before Recovery,
// Breakeven Simulation, Stop Tightening Simulation, Time vs Recovery.
// All percentages are PRICE percentages (NOT leverage-adjusted PnL).
// ---------------------------------------------------------------------------
function _fmtSec(n) {
  if (!Number.isFinite(n) || n <= 0) return "0s";
  if (n < 60) return `${n.toFixed(0)}s`;
  if (n < 3600) return `${(n / 60).toFixed(1)}m`;
  return `${(n / 3600).toFixed(1)}h`;
}

function _ExcursionStatRow({ label, stats, unit = "%" }) {
  return (
    <div className="grid grid-cols-6 text-xs font-mono items-center py-1">
      <span className="col-span-2 text-[#71717A]">{label}</span>
      <span className="text-right">{(stats?.mean ?? 0).toFixed(2)}{unit}</span>
      <span className="text-right">{(stats?.median ?? 0).toFixed(2)}{unit}</span>
      <span className="text-right">{(stats?.min ?? 0).toFixed(2)}{unit}</span>
      <span className="text-right">{(stats?.max ?? 0).toFixed(2)}{unit}</span>
    </div>
  );
}

function _ExcursionStatRowTime({ label, stats }) {
  return (
    <div className="grid grid-cols-6 text-xs font-mono items-center py-1">
      <span className="col-span-2 text-[#71717A]">{label}</span>
      <span className="text-right">{_fmtSec(stats?.mean ?? 0)}</span>
      <span className="text-right">{_fmtSec(stats?.median ?? 0)}</span>
      <span className="text-right">{_fmtSec(stats?.min ?? 0)}</span>
      <span className="text-right">{_fmtSec(stats?.max ?? 0)}</span>
    </div>
  );
}

function _BucketBars({ buckets, label }) {
  const order = ["<0.1%", "0.1-0.25%", "0.25-0.5%", "0.5-1%", ">1%"];
  const max = Math.max(1, ...order.map((k) => buckets?.[k] ?? 0));
  const total = order.reduce((s, k) => s + (buckets?.[k] ?? 0), 0);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">{label}</div>
      <div className="space-y-1">
        {order.map((b) => {
          const cnt = buckets?.[b] ?? 0;
          const pct = total > 0 ? (cnt / total) * 100 : 0;
          return (
            <div key={b} className="flex items-center gap-2 text-[11px] font-mono">
              <span className="w-16 text-[#A0A0AB]">{b}</span>
              <div className="flex-1 h-3 bg-[#1A1A22] rounded-sm overflow-hidden">
                <div className="h-full bg-[#3B82F6]/40 border-r border-[#3B82F6]"
                     style={{ width: `${(cnt / max) * 100}%` }} />
              </div>
              <span className="w-8 text-right text-[#A0A0AB]">{cnt}</span>
              <span className="w-12 text-right text-[#71717A]">{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BehaviorAnalyticsCard({ analytics }) {
  if (!analytics || ((analytics.losers?.count ?? 0) + (analytics.winners?.count ?? 0)) === 0) {
    return (
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4" data-testid="bt-behavior-empty">
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB] mb-1">Trade Behavior Analytics</div>
        <div className="text-xs text-[#71717A]">No closed trades — behavior metrics unavailable.</div>
      </div>
    );
  }
  const a = analytics;

  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm p-4 space-y-5" data-testid="bt-behavior-analytics">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB]">Trade Behavior Analytics</div>
        <div className="text-[11px] text-[#71717A] mt-1 max-w-3xl leading-snug">
          Path-level metrics built from per-candle MFE/MAE (max favorable/adverse excursion).
          {" "}<span className="text-[#F59E0B]">Caveat:</span> intra-candle ordering is unknown — when both
          extremes happened in the same bar, "first/second" sequence is indeterminate. All percentages
          are <span className="font-mono">price</span> %, not leverage-adjusted PnL %.
        </div>
      </div>

      {/* PART 1 — Favorable Before Failure */}
      <section data-testid="bt-bh-favorable-before-failure">
        <div className="text-[11px] uppercase tracking-widest text-[#A0A0AB] mb-2">
          1 · Favorable Before Failure (Losers)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3">
            <div className="text-[10px] uppercase text-[#71717A]">Headline</div>
            <div className="mt-1 text-2xl font-mono">
              {(a.losers?.with_favorable_first_pct ?? 0).toFixed(0)}%
            </div>
            <div className="text-[11px] text-[#A0A0AB]">
              of {a.losers?.count ?? 0} losers first moved in favor
            </div>
            <div className="text-[10px] text-[#71717A] mt-1 font-mono">
              {a.losers?.with_favorable_first_count ?? 0} / {a.losers?.count ?? 0} trades
            </div>
          </div>
          <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3 md:col-span-2">
            <div className="grid grid-cols-6 text-[10px] uppercase text-[#71717A] mb-1">
              <span className="col-span-2">Stat</span>
              <span className="text-right">avg</span>
              <span className="text-right">med</span>
              <span className="text-right">min</span>
              <span className="text-right">max</span>
            </div>
            <_ExcursionStatRow label="Favorable %" stats={a.losers?.favorable_excursion_stats_pct} />
            <_ExcursionStatRowTime label="Time → favorable" stats={a.losers?.time_to_favorable_seconds} />
            <_ExcursionStatRowTime label="Favorable → close" stats={a.losers?.time_from_favorable_to_close_seconds} />
          </div>
        </div>
        <div className="mt-3">
          <_BucketBars buckets={a.losers?.favorable_buckets} label="Distribution of favorable move (losers only)" />
        </div>
      </section>

      {/* PART 2 — Adverse Before Recovery */}
      <section data-testid="bt-bh-adverse-before-recovery">
        <div className="text-[11px] uppercase tracking-widest text-[#A0A0AB] mb-2">
          2 · Adverse Before Recovery (Winners)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3">
            <div className="text-[10px] uppercase text-[#71717A]">Headline</div>
            <div className="mt-1 text-2xl font-mono">
              {(a.winners?.with_adverse_first_pct ?? 0).toFixed(0)}%
            </div>
            <div className="text-[11px] text-[#A0A0AB]">
              of {a.winners?.count ?? 0} winners first went adverse
            </div>
            <div className="text-[10px] text-[#71717A] mt-1 font-mono">
              {a.winners?.with_adverse_first_count ?? 0} / {a.winners?.count ?? 0} trades
            </div>
          </div>
          <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3 md:col-span-2">
            <div className="grid grid-cols-6 text-[10px] uppercase text-[#71717A] mb-1">
              <span className="col-span-2">Stat</span>
              <span className="text-right">avg</span>
              <span className="text-right">med</span>
              <span className="text-right">min</span>
              <span className="text-right">max</span>
            </div>
            <_ExcursionStatRow label="Adverse %" stats={a.winners?.adverse_excursion_stats_pct} />
            <_ExcursionStatRowTime label="Time → adverse" stats={a.winners?.time_to_adverse_seconds} />
            <_ExcursionStatRowTime label="Adverse → close" stats={a.winners?.time_from_adverse_to_close_seconds} />
          </div>
        </div>
        <div className="mt-3">
          <_BucketBars buckets={a.winners?.adverse_buckets} label="Distribution of adverse move (winners only)" />
        </div>
      </section>

      {/* PART 3a — Breakeven simulation */}
      <section data-testid="bt-bh-breakeven-sim">
        <div className="text-[11px] uppercase tracking-widest text-[#A0A0AB] mb-2">
          3 · Breakeven Stop Simulation
        </div>
        <div className="text-[10px] text-[#71717A] mb-2">
          "After price moves +X% in our favor, move stop to entry."
          <span className="text-[#F59E0B] ml-1">winners_uncertain</span> means real winners whose MAE
          ≥ trigger — candle ambiguity prevents knowing if BE stop would have caught them.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-[10px] uppercase text-[#71717A] border-b border-border/40">
                <th className="text-left py-1">Trigger</th>
                <th className="text-right py-1">Losers saved</th>
                <th className="text-right py-1">PnL recovered</th>
                <th className="text-right py-1">Winners untouched</th>
                <th className="text-right py-1">Winners uncertain</th>
                <th className="text-right py-1">Untriggered</th>
              </tr>
            </thead>
            <tbody>
              {(a.breakeven_simulations || []).map((s) => (
                <tr key={s.trigger_pct} className="border-b border-border/20">
                  <td className="py-1">+{s.trigger_pct}%</td>
                  <td className="text-right py-1 text-[#22C55E]">{s.losers_saved}</td>
                  <td className="text-right py-1 text-[#22C55E]">+${(s.losers_saved_pnl_usdt || 0).toFixed(2)}</td>
                  <td className="text-right py-1">{s.winners_unaffected}</td>
                  <td className="text-right py-1 text-[#F59E0B]">{s.winners_uncertain}</td>
                  <td className="text-right py-1 text-[#71717A]">{s.untriggered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* PART 3b — Tighter stop simulation */}
      <section data-testid="bt-bh-tighter-stop-sim">
        <div className="text-[11px] uppercase tracking-widest text-[#A0A0AB] mb-2">
          4 · Stop Tightening Simulation
        </div>
        <div className="text-[10px] text-[#71717A] mb-2">
          Hypothetical fixed stop at -X% PRICE move from entry (independent of leverage).
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-[10px] uppercase text-[#71717A] border-b border-border/40">
                <th className="text-left py-1">Stop @</th>
                <th className="text-right py-1">Winners lost</th>
                <th className="text-right py-1">PnL given up</th>
                <th className="text-right py-1">Losers capped</th>
                <th className="text-right py-1">PnL saved</th>
                <th className="text-right py-1">Hypo PnL</th>
                <th className="text-right py-1">Hypo win-rate</th>
                <th className="text-right py-1">Hypo PF</th>
              </tr>
            </thead>
            <tbody>
              {(a.tighter_stop_simulations || []).map((s) => {
                const net = s.hypothetical_total_pnl_usdt || 0;
                return (
                  <tr key={s.stop_pct} className="border-b border-border/20">
                    <td className="py-1">-{s.stop_pct}%</td>
                    <td className="text-right py-1 text-[#EF4444]">{s.winners_lost_to_stop}</td>
                    <td className="text-right py-1 text-[#EF4444]">-${(s.winners_lost_pnl_usdt || 0).toFixed(2)}</td>
                    <td className="text-right py-1 text-[#22C55E]">{s.losers_capped}</td>
                    <td className="text-right py-1 text-[#22C55E]">+${(s.losers_capped_pnl_delta_usdt || 0).toFixed(2)}</td>
                    <td className={`text-right py-1 ${net >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                      {net >= 0 ? "+" : ""}${net.toFixed(2)}
                    </td>
                    <td className="text-right py-1">{(s.hypothetical_win_rate || 0).toFixed(1)}%</td>
                    <td className="text-right py-1">{(s.hypothetical_profit_factor || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* PART 4 — Time vs Recovery + correlations + insights */}
      <section data-testid="bt-bh-time-vs-recovery">
        <div className="text-[11px] uppercase tracking-widest text-[#A0A0AB] mb-2">
          5 · Time vs Recovery Behavior
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3">
            <div className="text-[10px] uppercase text-[#71717A] mb-2">Pearson correlations</div>
            <div className="grid grid-cols-2 gap-y-1 text-xs font-mono">
              {Object.entries(a.correlations || {}).map(([k, v]) => (
                <div key={k} className="contents">
                  <span className="text-[#A0A0AB]">{k}</span>
                  <span className={`text-right ${Math.abs(v) > 0.3
                    ? (v > 0 ? "text-[#22C55E]" : "text-[#EF4444]") : "text-[#71717A]"}`}>
                    {(v ?? 0).toFixed(3)}
                  </span>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-[#71717A] mt-2 leading-snug">
              |r| &gt; 0.3 highlighted. Pearson correlation between trade-level
              duration / MFE / MAE / realized PnL.
            </div>
          </div>
          <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3">
            <div className="text-[10px] uppercase text-[#71717A] mb-2">Insights</div>
            <ul className="text-xs space-y-1.5 list-disc list-inside text-[#A0A0AB]">
              {(a.insights || []).map((s, i) => (<li key={i}>{s}</li>))}
              {(!a.insights || a.insights.length === 0) && (
                <li className="text-[#71717A] list-none">No notable patterns detected.</li>
              )}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}



// ---------------------------------------------------------------------------
// TagChips — renders a compact pill-row for a list of `{key, value, kind}`.
// `compact` mode is used in history table cells (max 6 chips, smaller font);
// the full mode is used inside ConfigViewer.
// ---------------------------------------------------------------------------
function TagChips({ tags, compact = false }) {
  const list = Array.isArray(tags) ? tags : [];
  if (list.length === 0) {
    return <span className="text-[10px] text-[#52525B] font-mono">—</span>;
  }
  const trimmed = compact ? list.slice(0, 6) : list;
  const overflow = compact ? Math.max(0, list.length - 6) : 0;
  return (
    <div className="flex flex-wrap gap-1" data-testid="bt-tag-chips">
      {trimmed.map((t, i) => (
        <span
          key={`${t.key}-${i}`}
          data-testid={`bt-tag-${t.key}`}
          title={`${t.key}=${t.value}${t.kind ? ` · ${t.kind}` : ""}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-mono ${compact ? "text-[10px]" : "text-[11px]"} border ${
            t.kind === "warning"
              // Amber palette flags execution combinations that
              // can't be replayed live (e.g. LBank-unrealistic).
              ? "bg-[#F59E0B]/15 border-[#F59E0B]/50 text-[#FCD34D]"
              : t.kind === "strategy"
                ? "bg-[#22C55E]/10 border-[#22C55E]/40 text-[#86EFAC]"
                : "bg-[#3B82F6]/10 border-[#3B82F6]/40 text-[#93C5FD]"
          }`}
        >
          <span className="text-[#71717A]">{t.key}</span>
          <span>{t.value}</span>
        </span>
      ))}
      {overflow > 0 ? (
        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] text-[#71717A] font-mono">+{overflow}</span>
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// ConfigViewer — expandable panel showing the FULL immutable config
// snapshot saved with each backtest. Two columns:
//   1. Execution config (cfg)         → leverage / sizing / fees / slippage
//   2. Strategy snapshot (profile)    → conditions / params / exit rules
// Plus an `engine_version` line for engine-math fingerprinting and the two
// pre-fill buttons (Re-run, Duplicate). Both buttons are duplicates of the
// top-of-result-page buttons — placed here too because operators looking at
// the full config are most likely to want to clone it.
// ---------------------------------------------------------------------------
function ConfigViewer({ doc, onRerun, onDuplicate }) {
  const [open, setOpen] = useState(false);
  const cfg = doc?.config || {};
  const snap = doc?.strategy_config_snapshot || {};
  const ev = doc?.engine_version || null;
  // Tags: prefer server-saved, else live-derive (mirrors top-level tagsOf).
  const tags = Array.isArray(doc?.config_tags) && doc.config_tags.length > 0
    ? doc.config_tags
    : null;

  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm" data-testid="bt-config-viewer">
      <button
        type="button"
        data-testid="bt-config-viewer-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#1A1A22] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">View Full Configuration</span>
          {tags ? <TagChips tags={tags.slice(0, 6)} compact /> : null}
        </div>
        <span className="text-[10px] text-[#71717A] font-mono">{open ? "▾ hide" : "▸ show"}</span>
      </button>
      {open ? (
        <div className="border-t border-border px-4 py-4 space-y-4" data-testid="bt-config-viewer-body">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] text-[#71717A] leading-snug max-w-2xl">
              Immutable snapshot saved at run time. Editing the live strategy
              later does <span className="text-[#E4E4E7]">not</span> change this.
              "Re-run" / "Duplicate" pre-fill the form with this exact config + the
              <span className="font-mono text-[#86EFAC]"> inline strategy snapshot</span> —
              you can review/edit before clicking Run.
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="bt-config-viewer-rerun"
                size="sm" variant="outline"
                onClick={onRerun}
                className="rounded-sm border-[#22C55E]/40 bg-[#1A1A22] hover:bg-[#22C55E]/10 text-[#22C55E] text-xs"
              >
                <RefreshCw size={13} strokeWidth={2} className="mr-1.5" /> Re-run (pre-fill)
              </Button>
              <Button
                data-testid="bt-config-viewer-duplicate"
                size="sm" variant="outline"
                onClick={onDuplicate}
                className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
              >
                <Copy size={13} strokeWidth={2} className="mr-1.5" /> Duplicate config
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Execution config */}
            <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Execution config</div>
              <table className="w-full text-xs font-mono">
                <tbody>
                  {Object.entries(cfg).filter(([k]) => k !== "id").map(([k, v]) => (
                    <tr key={k} className="border-b border-border/20 last:border-b-0">
                      <td className="py-1 text-[#71717A] pr-3">{k}</td>
                      <td className="py-1 text-right break-all">{
                        v === null || v === undefined ? "—"
                        : typeof v === "object" ? JSON.stringify(v)
                        : String(v)
                      }</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Strategy snapshot */}
            <div className="bg-[#1A1A22] border border-border/50 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2 flex items-center gap-2">
                Strategy snapshot
                {snap?.id ? <span className="font-mono text-[#86EFAC] normal-case tracking-normal text-[10px]">id={snap.id.slice(0, 8)}</span> : null}
              </div>
              {!snap || Object.keys(snap).length === 0 ? (
                <div className="text-xs text-[#71717A]">No snapshot saved.</div>
              ) : (
                <div className="text-xs font-mono space-y-1">
                  <div><span className="text-[#71717A]">name:</span> {snap.name}</div>
                  <div><span className="text-[#71717A]">timeframe:</span> {snap.timeframe}</div>
                  <div><span className="text-[#71717A]">combinator:</span> {snap.combinator}</div>
                  <div><span className="text-[#71717A]">cooldown:</span> {snap.cooldown_minutes ?? "—"} min</div>
                  <div>
                    <span className="text-[#71717A]">max_long:</span> {cfg?.max_open_long_positions ?? 1}
                    <span className="text-[#71717A] ml-3">max_short:</span> {cfg?.max_open_short_positions ?? 1}
                    {(cfg?.max_open_long_positions ?? 1) === 1
                      && (cfg?.max_open_short_positions ?? 1) === 1
                      ? <span className="text-[10px] text-[#71717A] ml-2">(no stacking)</span>
                      : <span className="text-[10px] text-[#86EFAC] ml-2">(stacking enabled)</span>}
                  </div>
                  <div><span className="text-[#71717A]">legacy max_open:</span> {snap.max_open_positions ?? "—"}</div>
                  <div className="pt-2 text-[#71717A]">conditions ({(snap.conditions || []).length}):</div>
                  <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {(snap.conditions || []).map((c, i) => (
                      <div key={c.id || i} className={`p-1.5 rounded-sm border ${c.enabled === false ? "border-border/30 opacity-50" : "border-border/60 bg-[#0F0F14]"}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-[#86EFAC]">{c.type}</span>
                          {c.direction ? <span className="text-[10px] text-[#71717A]">{c.direction}</span> : null}
                          {c.enabled === false ? <span className="text-[10px] text-[#EF4444]">disabled</span> : null}
                        </div>
                        {c.params ? (
                          <div className="text-[10px] text-[#A0A0AB] mt-1 break-all">
                            {Object.entries(c.params).map(([k, v]) => (
                              <span key={k} className="mr-2">
                                <span className="text-[#71717A]">{k}=</span>{String(v)}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  {snap.exit_rules ? (
                    <div className="pt-2">
                      <div className="text-[#71717A]">exit_rules:</div>
                      <div className="text-[10px] text-[#A0A0AB] break-all">
                        {Object.entries(snap.exit_rules).map(([k, v]) => (
                          <span key={k} className="mr-2"><span className="text-[#71717A]">{k}=</span>{String(v)}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="text-[10px] text-[#71717A] font-mono flex items-center gap-2">
            <span>engine_version:</span>
            <span className="text-[#A0A0AB]">{ev || "(not recorded — predates v2.4.0)"}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
